import JSZip from 'jszip';
import mammoth from 'mammoth';
import omml2mathml from 'omml2mathml';
import { MathMLToLaTeX } from 'mathml-to-latex';

// ============================================================
// DANH SÁCH CÁC KÝ TỰ ĐÁP ÁN TRẮC NGHIỆM CHUẨN
// Dùng làm bảng tham chiếu để gán/ánh xạ thứ tự lựa chọn (0,1,2,3...) sang
// ký tự đáp án tương ứng (A, B, C, D), ví dụ khi cần đổi sub-item dạng số
// (1, 2, 3...) hoặc a/b/c/d về đúng định dạng đáp án trắc nghiệm chuẩn.
// ============================================================
const ANSWER_LETTERS = ['A', 'B', 'C', 'D'];

// ============================================================
// CHUẨN HÓA GIÁ TRỊ ĐẦU VÀO VỀ CHUỖI ĐÃ TRIM
// Ép kiểu giá trị bất kỳ (kể cả null/undefined) về string an toàn bằng
// toán tử "??" (nullish coalescing), sau đó cắt bỏ khoảng trắng thừa ở đầu/
// cuối. Dùng làm bước tiền xử lý chung trước khi đưa vào các regex khác,
// tránh lỗi runtime khi gặp dòng rỗng, null hoặc undefined.
// Ví dụ: getTrimmedString(null) -> ""; getTrimmedString("  abc  ") -> "abc"
// ============================================================
const getTrimmedString = (value) => String(value ?? '').trim();

// ============================================================
// HÀM SINH REGEX DÙNG CHUNG CHO CÁC DÒNG DẠNG "NHÃN: NỘI DUNG"
// Nhận vào một pattern liệt kê các biến thể của nhãn (labelsPattern, dạng
// chuỗi regex nối bằng "|", vd: "giai\\s*thich|giải\\s*thích|explanation"),
// trả về một RegExp dùng để nhận diện dòng bắt đầu bằng nhãn đó, có thể theo
// sau bởi dấu ":" hoặc "-" và phần nội dung (không bắt buộc).
//
// Regex sinh ra có cấu trúc: ^(?:<các nhãn>)\s*(?:[:\-]\s*(.*))?$
//   - Cho phép dòng chỉ chứa nhãn (không có nội dung theo sau), khi đó
//     nhóm capture sẽ là undefined.
//   - Cho phép nhãn theo sau bởi ":" hoặc "-" rồi đến nội dung.
//   - Cờ "i": không phân biệt hoa/thường.
//
// Nhóm capture (nhóm 1): phần nội dung đứng sau nhãn, nếu có.
//
// Ví dụ: makeLabelRegex('hint|goi\\s*y') sẽ khớp:
//   "Hint: Chọn đáp án gần nghĩa nhất" -> nhóm1 = "Chọn đáp án gần nghĩa nhất"
//   "Gợi ý" (không có nội dung theo sau) -> nhóm1 = undefined
// ============================================================
const makeLabelRegex = (labelsPattern) => new RegExp(
    `^(?:${labelsPattern})\\s*(?:[:\\-]\\s*(.*))?$`,
    'i'
);

// ============================================================
// NHẬN DIỆN DÒNG MỞ ĐẦU CÂU HỎI
// Khớp các dạng: "Câu 12:", "Câu hỏi 12.", "Question 12)", có thể có
// số thứ tự đứng trước dạng "(1) Câu 12: ..." hoặc "[1] Question 12: ...".
// Mỗi pattern trả về 2 nhóm capture:
//   Nhóm 1: số thứ tự của câu hỏi (vd: "12")
//   Nhóm 2: phần nội dung còn lại trên cùng dòng, sau nhãn "Câu 12:" (có thể rỗng
//           nếu nội dung câu hỏi nằm ở dòng kế tiếp)
// ============================================================
const questionStartPatterns = [
    // Dạng "Câu 12:", "Cau 12.", "câu 12)", "câu 12 -"
    // Ví dụ khớp: "Câu 12: Chọn đáp án đúng" -> nhóm1="12", nhóm2="Chọn đáp án đúng"
    /^(?:(?:\(|\[)?\d+(?:\)|\])?[.:)-]?\s*)?(?:cau|câu)\s*(\d+)\s*[.:)-]?\s*(.*)$/i,

    // Dạng "Câu hỏi 12:", "Cau hoi 12."
    // Ví dụ khớp: "Câu hỏi 12: Nội dung..." -> nhóm1="12", nhóm2="Nội dung..."
    /^(?:(?:\(|\[)?\d+(?:\)|\])?[.:)-]?\s*)?(?:cau|câu)\s*h(?:oi|ỏi)\s*(\d+)\s*[.:)-]?\s*(.*)$/i,

    // Dạng tiếng Anh "Question 12)", "Question No. 12:", "Question Number 12."
    // Ví dụ khớp: "Question 12) What is..." -> nhóm1="12", nhóm2="What is..."
    /^(?:(?:\(|\[)?\d+(?:\)|\])?[.:)-]?\s*)?(?:question)\s*(?:no\.?|number)?\s*(\d+)\s*[.:)-]?\s*(.*)$/i,
];

// ============================================================
// NHẬN DIỆN DÒNG GHI CHÚ GIẢI THÍCH ĐÁP ÁN
// Khớp các nhãn: "Giải thích:", "Giai thich:", "Trả lời:", "Tra loi:",
// "Explanation:" — thường đứng ở đầu dòng, theo sau là nội dung giải thích.
// ============================================================
const explanationRegex = makeLabelRegex('giai\\s*thich|giải\\s*thích|tra\\s*loi|trả\\s*lời|explanation');

// ============================================================
// NHẬN DIỆN DÒNG GHI CHÚ GỢI Ý
// Khớp các nhãn: "Gợi ý:", "Goi y:", "Hint:", "Suggest:" — dùng để đánh dấu
// phần gợi ý làm bài, tách biệt với nội dung câu hỏi và đáp án.
// ============================================================
const hintRegex = makeLabelRegex('goi\\s*y|gợi\\s*ý|suggest|hint');

// ============================================================
// NHẬN DIỆN DÒNG CẤU HÌNH LOẠI CÂU HỎI
// Khớp các nhãn: "Loại:", "Loai:", "Type:" — dùng để chỉ định loại câu hỏi
// (trắc nghiệm, đúng/sai, tự luận...) được khai báo tường minh trong đề.
// ============================================================
const typeRegex = makeLabelRegex('loai|loại|type');

// ============================================================
// NHẬN DIỆN DÒNG ĐÁP ÁN MẪU (dùng cho câu trả lời ngắn/tự luận)
// Khớp các nhãn: "Đáp án mẫu:", "Dap an mau:", "Sample answer:" — nội dung
// theo sau là câu trả lời mẫu tham khảo, không phải đáp án trắc nghiệm.
// ============================================================
const sampleAnswerRegex = makeLabelRegex('dap\\s*an\\s*mau|đáp\\s*án\\s*mẫu|sample\\s*answer');

// ============================================================
// NHẬN DIỆN DÒNG CHỈ RÕ ĐÁP ÁN ĐÚNG (dùng cho câu trắc nghiệm/đúng-sai)
// Khớp các nhãn: "Đáp án đúng:", "Dap an dung:", "Correct:" — nội dung theo
// sau thường là (các) ký tự đáp án, vd: "A", "B, D", "Đúng".
// ============================================================
const correctAnswerRegex = makeLabelRegex('dap\\s*an\\s*dung|đáp\\s*án\\s*đúng|correct');

// ============================================================
// NHẬN DIỆN TIÊU ĐỀ SECTION/PHẦN LỚN
// Khớp các dạng: "Phần 1", "Phan I", "Part II", "Section a", có thể có số
// thứ tự đứng trước dạng "1. Phần 2 - Đọc hiểu".
// 3 nhóm capture:
//   Nhóm 1: toàn bộ cụm tiêu đề section (vd: "Phần 2")
//   Nhóm 2: mã/số của section — có thể là số ("2"), số La Mã ("ii"), hoặc chữ ("a")
//   Nhóm 3: phần mô tả đi kèm sau dấu phân cách, nếu có (vd: "Đọc hiểu")
// ============================================================
const sectionHeaderRegex = /^((?:\d+\s*[.)-]\s*)?(?:phần|phan|part|section)\s*(\d+|[ivxlcdm]+|[a-z]))\b(?:\s*[.:)-]?\s*(.*))?$/i;

// ============================================================
// NHẬN DIỆN DÒNG HƯỚNG DẪN/ĐOẠN VĂN CHUNG (PASSAGE) CHEN GIỮA CÁC CÂU HỎI
// Dùng để tách các dòng dẫn nhập/hướng dẫn ra khỏi nội dung câu hỏi trước đó,
// tránh bị nối nhầm vào cuối câu hỏi liền trước.
// Bao gồm cả mẫu tiếng Anh (vd: "Mark the letter...", "Read the following...")
// lẫn tiếng Việt (vd: "Dựa vào thông tin sau...", "Đọc đoạn văn sau và trả lời
// các câu hỏi...").
// ============================================================
const sectionBreakRegex = /^(?:mark\s+the\s+letter|read\s+the\s+following|join\s+our|announcement\b|do\s+you\s+have\s+a\s+passion\b|(?:đây|day)\s+l(?:à|a)\s+th(?:ô|o)ng\s+tin\s+d(?:ù|u)ng\s+chung|d(?:ù|u)ng\s+th(?:ô|o)ng\s+tin\s+sau|d(?:ự|u)a\s+(?:v(?:à|a)o|theo)\s+th(?:ô|o)ng\s+tin\s+sau|tr(?:ả|a)\s+l(?:ờ|o)i\s+c(?:á|a)c\s+c(?:â|a)u\s+h(?:ỏ|o)i\s+sau)/i;

// ============================================================
// NHẬN DIỆN SUB-ITEM DẠNG CHỮ CÁI THƯỜNG
// Khớp các dòng con đánh dấu bằng chữ cái: "a) ...", "b. ...", dùng để nối
// vào thân câu hỏi cha (thường gặp trong câu hỏi nhiều ý nhỏ a/b/c/d).
// Nhóm 1: chữ cái đánh dấu (vd: "a")
// Nhóm 2: nội dung của sub-item
// ============================================================
const subItemRegex = /^([a-z])\s*[.)]\s+(.+)$/;

// ============================================================
// NHẬN DIỆN SUB-ITEM DẠNG SỐ
// Khớp các dòng con đánh số: "1) ...", "2. ...", dùng khi đề đánh số thay vì
// chữ cái cho các ý nhỏ — cần được chuyển đổi ngược về dạng a/b/c khi chuẩn hoá.
// Nhóm 1: số thứ tự (vd: "1")
// Nhóm 2: nội dung của sub-item
// ============================================================
const numericSubItemRegex = /^(\d+)\s*[.)]\s+(.+)$/;

// ============================================================
// NHẬN DIỆN CÂU HỎI ĐÁNH SỐ TỰ ĐỘNG (không có nhãn "Câu")
// Dùng cho các đề không ghi rõ "Câu 12:" mà chỉ đánh số thẳng: "12. Nội dung
// câu hỏi...". Đây là fallback khi questionStartPatterns không khớp.
// Nhóm 1: số thứ tự câu hỏi
// Nhóm 2: nội dung câu hỏi theo sau
// ============================================================
const numberedQuestionStartRegex = /^(\d+)\s*[.)]\s+(.+)$/;

// ============================================================
// LOẠI BỎ KÝ TỰ TRANG TRÍ Ở ĐẦU DÒNG
// Dùng trước khi nhận diện dòng hướng dẫn/passage, để các ký tự định dạng
// Markdown hoặc dấu ngoặc trích dẫn (vd: "**", "-", ">", dấu nháy kép/đơn
// thẳng và cong) không làm sai lệch kết quả match của regex.
// Ví dụ: "**- "Read the following..."" -> "Read the following..."
// ============================================================
const stripLeadingDecorators = (line) => getTrimmedString(line).replace(/^[*_`~#>\-\s"'“”‘’]+/, '');

// ============================================================
// CHUẨN HÓA KÝ TỰ XUỐNG DÒNG
// Đưa mọi kiểu xuống dòng (\r\n của Windows, \r đơn lẻ của Mac cũ) về thống
// nhất thành \n, đảm bảo việc tách dòng (split theo \n) cho kết quả ổn định
// trên mọi nền tảng/nguồn file khác nhau.
// ============================================================
const normalizeLineBreaks = (text) => text.replace(/\r\n?/g, '\n');

// ============================================================
// CHUẨN HÓA CÁC KÝ TỰ ĐẶC BIỆT THƯỜNG GẶP TỪ VĂN BẢN WORD
// Xử lý các ký tự "ẩn" hoặc ký tự đặc biệt hay xuất hiện khi copy/convert từ
// file DOCX, để các regex nhận diện nhãn (label) phía sau match được chính xác.
// Các bước xử lý theo thứ tự:
//   1. Chuẩn hóa Unicode về dạng NFKC (gộp các ký tự tổ hợp/tương đương
//      thành 1 dạng biểu diễn thống nhất).
//   2. Xóa các ký tự zero-width (zero-width space/joiner/non-joiner, BOM)
//      thường bị chèn ngầm khi copy nội dung từ Word.
//   3. Đưa các loại khoảng trắng đặc biệt (non-breaking space, en/em space,
//      khoảng trắng Unicode khác...) về dấu cách thường (U+0020).
//   4. Đưa các loại dấu gạch ngang dài/gạch trừ đặc biệt (en dash, em dash,
//      minus sign, fullwidth hyphen...) về dấu gạch nối "-" thông thường,
//      để regex nhãn dạng "Đáp án - đúng" hay tương tự dễ match hơn.
//   5. Đưa dấu hai chấm dạng fullwidth "：" về dấu hai chấm thường ":".
// ============================================================
const normalizeParserLine = (value) => getTrimmedString(value)
    .normalize('NFKC')
    // Loại bỏ zero-width chars hay bị chèn khi copy/convert DOCX.
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    // Đưa các khoảng trắng đặc biệt về dấu cách thường.
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
    // Chuẩn hóa các dấu gạch dài/gạch trừ đặc biệt về dấu gạch nối thường,
    // để regex nhãn dễ match hơn.
    .replace(/[\u2012\u2013\u2014\u2015\u2212\uFF0D]/g, '-')
    // Chuẩn hóa dấu hai chấm fullwidth về dấu hai chấm thường.
    .replace(/\uFF1A/g, ':');

// Tim cac khoang chi so nam trong bieu thuc LaTeX viet bang $...$ hoac $$...$$.
const getMathRanges = (input) => {
    const ranges = [];
    const text = String(input || '');
    let i = 0;
    let active = null;

    while (i < text.length) {
        if (text[i] !== '$') {
            i += 1;
            continue;
        }

        const isDouble = i + 1 < text.length && text[i + 1] === '$';
        const delimiter = isDouble ? '$$' : '$';
        const tokenLength = isDouble ? 2 : 1;

        if (!active) {
            active = { start: i, delimiter };
            i += tokenLength;
            continue;
        }

        if (active.delimiter === delimiter) {
            ranges.push({ start: active.start, end: i + tokenLength - 1 });
            active = null;
            i += tokenLength;
            continue;
        }

        // Cong thuc mo bang "$" nhung gap "$$": dau "$" dau tien van la dau dong,
        // phan "$" con lai duoc xet lai o vong lap sau (co the mo cong thuc moi).
        if (active.delimiter === '$') {
            ranges.push({ start: active.start, end: i });
            active = null;
            i += 1;
            continue;
        }

        i += tokenLength;
    }

    return ranges;
};

const isIndexInMathRange = (index, ranges) => ranges.some((range) => index >= range.start && index <= range.end);

const DOCX_W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const DOCX_M_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/math';
const XML_NS = 'http://www.w3.org/XML/1998/namespace';
// Hang so nodeType, dung thay cho bien toan cuc `Node` (khong ton tai ngoai trinh duyet).
const ELEMENT_NODE_TYPE = 1;
const TEXT_NODE_TYPE = 3;
const DOCX_MATH_TOKEN_PREFIX = 'DOCX_MATH_TOKEN_';
// Namespace cua anh kieu VML cu (<w:pict><v:imagedata r:id="rIdN"/>) va cua quan he r:id.
const VML_NS = 'urn:schemas-microsoft-com:vml';
const DOCX_R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const DOCX_IMAGE_TOKEN_PREFIX = 'DOCX_IMAGE_TOKEN_';
// Anh DrawingML (<wp:inline>/<wp:anchor>).
const DOCX_WP_NS = 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing';
const DOCX_IMAGE_SIZE_KEY_PREFIX = 'DOCX_IMAGE_SIZE_';

// ============================================================
// KICH THUOC HIEN THI CUA ANH TRONG WORD
// File anh luu trong DOCX thuong co do phan giai cao hon nhieu so voi kich thuoc
// Word hien thi (vd cong thuc dang anh: Word hien 61x20 px, file anh 945x308 px).
// Kich thuoc hien thi nam o <wp:extent> (anh DrawingML) hoac style cua <v:shape>
// (anh VML). mammoth bo qua ca hai, nen trinh duyet hien anh theo so pixel cua
// file -> cong thuc to gap nhieu lan chu xung quanh.
// ============================================================
const EMU_PER_PX = 9525;
const CSS_LENGTH_TO_PX = {
    px: 1,
    pt: 96 / 72,
    pc: 16,
    in: 96,
    cm: 96 / 2.54,
    mm: 96 / 25.4,
};

const roundPx = (value) => Math.round(value * 100) / 100;

const toImageSize = (width, height) => (
    width > 0 && height > 0 ? { width: roundPx(width), height: roundPx(height) } : null
);

const findDirectChild = (node, namespace, localName) => Array.from(node?.childNodes || []).find(
    (child) => child.nodeType === ELEMENT_NODE_TYPE && child.namespaceURI === namespace && child.localName === localName
) || null;

// <wp:extent cx="584200" cy="187325"/> (don vi EMU) -> { width, height } px.
const readDrawingImageSize = (drawingNode) => {
    const extent = findDirectChild(drawingNode, DOCX_WP_NS, 'extent');
    return toImageSize(
        Number(extent?.getAttribute('cx')) / EMU_PER_PX,
        Number(extent?.getAttribute('cy')) / EMU_PER_PX
    );
};

// style="width:45.75pt;height:15pt;..." cua <v:shape> -> { width, height } px.
const readVmlShapeImageSize = (shapeNode) => {
    const style = String(shapeNode?.getAttribute?.('style') || '');
    const readLength = (name) => {
        const match = style.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([\\d.]+)\\s*(px|pt|pc|in|cm|mm)?`, 'i'));
        return match ? Number(match[1]) * (CSS_LENGTH_TO_PX[(match[2] || 'px').toLowerCase()] || 0) : 0;
    };

    return toImageSize(readLength('width'), readLength('height'));
};

const buildImageSizeStyle = (size) => (size ? `width: ${size.width}px; height: ${size.height}px` : '');

// ============================================================
// CHUYEN KICH THUOC WORD QUA MAMMOTH
// mammoth khong cho biet anh nao la anh nao trong convertImage, chi dua ra alt text
// (docPr descr/title voi DrawingML, o:title voi VML). Ta tam thay alt text bang
// mot ma rieng tro toi kich thuoc da doc, convertImage doc lai kich thuoc qua ma
// do roi tra alt text goc ve cho the <img>.
// ============================================================
const tagImagesWithWordSize = (xmlDoc) => {
    const imageSizes = new Map();
    const createKey = () => `${DOCX_IMAGE_SIZE_KEY_PREFIX}${imageSizes.size}__`;

    ['inline', 'anchor'].forEach((localName) => {
        Array.from(xmlDoc.getElementsByTagNameNS(DOCX_WP_NS, localName)).forEach((drawingNode) => {
            const size = readDrawingImageSize(drawingNode);
            const docPr = findDirectChild(drawingNode, DOCX_WP_NS, 'docPr');

            if (!size || !docPr) {
                return;
            }

            // Cung thu tu uu tien voi mammoth: descr, khong co thi title.
            const descr = docPr.getAttribute('descr') || '';
            const key = createKey();
            imageSizes.set(key, { ...size, altText: getTrimmedString(descr) ? descr : (docPr.getAttribute('title') || '') });
            docPr.setAttribute('descr', key);
        });
    });

    // Khong ap dung cho anh VML con lai (vd anh xem truoc cua cong thuc MathType trong
    // <w:object>): mammoth doc alt text cua VML qua "o:title" nhung bang namespace cua
    // no khong co prefix "o", nen alt text do khong bao gio toi convertImage. Anh xem
    // truoc cua MathType lai hau nhu luon la WMF/EMF, trinh duyet khong hien duoc.
    // Anh <w:pict> da tu mang kich thuoc trong token (replacePictImagesWithTokens).

    return imageSizes;
};

// Suy ra content type tu duoi file anh trong goi DOCX.
const IMAGE_CONTENT_TYPES = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    wmf: 'image/x-wmf',
    emf: 'image/x-emf',
};

const getImageContentType = (path) => {
    const extension = String(path || '').split('.').pop().toLowerCase();
    return IMAGE_CONTENT_TYPES[extension] || 'application/octet-stream';
};

const isDescendantOfMathPara = (node) => {
    let current = node?.parentNode || null;

    while (current) {
        if (current.nodeType === 1 && current.namespaceURI === DOCX_M_NS && current.localName === 'oMathPara') {
            return true;
        }

        current = current.parentNode || null;
    }

    return false;
};

// Tao rieng the <w:t> (dung khi thay the mot phan tu nam san trong <w:r>).
const createWordText = (doc, text) => {
    const textNode = doc.createElementNS(DOCX_W_NS, 'w:t');
    textNode.setAttributeNS(XML_NS, 'xml:space', 'preserve');
    textNode.textContent = text;
    return textNode;
};

const createWordTextRun = (doc, text) => {
    const run = doc.createElementNS(DOCX_W_NS, 'w:r');
    const textNode = doc.createElementNS(DOCX_W_NS, 'w:t');
    textNode.setAttributeNS(XML_NS, 'xml:space', 'preserve');
    textNode.textContent = text;
    run.appendChild(textNode);
    return run;
};

const createWordParagraph = (doc, text) => {
    const paragraph = doc.createElementNS(DOCX_W_NS, 'w:p');
    paragraph.appendChild(createWordTextRun(doc, text));
    return paragraph;
};

const createWordLineBreakRun = (doc) => {
    const run = doc.createElementNS(DOCX_W_NS, 'w:r');
    run.appendChild(doc.createElementNS(DOCX_W_NS, 'w:br'));
    return run;
};

// <w:br/> thuong (Shift+Enter) va <w:cr/> la xuong dong; <w:br w:type="page"/> thi khong.
const isWordLineBreakNode = (node) => {
    if (node?.namespaceURI !== DOCX_W_NS) {
        return false;
    }

    if (node.localName === 'cr') {
        return true;
    }

    if (node.localName !== 'br') {
        return false;
    }

    const type = node.getAttributeNS(DOCX_W_NS, 'type') || node.getAttribute('w:type');
    return !type || type === 'textWrapping';
};

// Phan tu tao ra noi dung nhin thay duoc trong doan van: chu, xuong dong, anh, cong thuc.
const isVisibleWordContentNode = (node) => {
    if (node?.nodeType !== ELEMENT_NODE_TYPE) {
        return false;
    }

    if (node.namespaceURI === DOCX_M_NS) {
        return node.localName === 'oMath' || node.localName === 'oMathPara';
    }

    if (node.namespaceURI !== DOCX_W_NS) {
        return false;
    }

    if (node.localName === 't') {
        return Boolean(getTrimmedString(node.textContent));
    }

    return isWordLineBreakNode(node) || ['sym', 'drawing', 'pict', 'object'].includes(node.localName);
};

// Phan tu nhin thay dau tien (hoac cuoi cung) ben trong node, tinh ca chinh node.
const findEdgeVisibleNode = (node, fromEnd) => {
    if (isVisibleWordContentNode(node)) {
        return node;
    }

    const children = Array.from(node?.childNodes || []);
    if (fromEnd) {
        children.reverse();
    }

    for (let i = 0; i < children.length; i += 1) {
        const found = findEdgeVisibleNode(children[i], fromEnd);
        if (found) {
            return found;
        }
    }

    return null;
};

// Noi dung nhin thay ngay truoc/sau node, chi tim trong pham vi doan van chua no.
const findAdjacentVisibleNode = (node, paragraph, fromEnd) => {
    let current = node;

    while (current && current !== paragraph) {
        let sibling = fromEnd ? current.previousSibling : current.nextSibling;

        while (sibling) {
            const found = findEdgeVisibleNode(sibling, fromEnd);
            if (found) {
                return found;
            }

            sibling = fromEnd ? sibling.previousSibling : sibling.nextSibling;
        }

        current = current.parentNode;
    }

    return null;
};

const findWordParagraphAncestor = (node) => {
    let current = node?.parentNode || null;

    while (current && current.nodeType === ELEMENT_NODE_TYPE) {
        if (current.namespaceURI === DOCX_W_NS && current.localName === 'p') {
            return current;
        }

        current = current.parentNode;
    }

    return null;
};

// ============================================================
// THAY CONG THUC HIEN THI (<m:oMathPara>) BANG TOKEN
// <m:oMathPara> luon nam BEN TRONG <w:p>, va rat hay nam chung doan voi chu:
//     <w:p> "Giai thich: Ta co" <w:br/> <m:oMathPara/> "Khi do, ta co" <w:br/> <m:oMathPara/> </w:p>
// Truoc day moi <m:oMathPara> bi thay bang mot <w:p> moi => <p> long trong <p>.
// Trinh duyet tu dong dong <p> ngoai khi gap <p> trong, nen:
//   - "Khi do, ta co" thanh text troi noi ngoai moi <p> -> bi mat han;
//   - dong "Giai thich: Ta co<br>" con dong trong o cuoi -> parser thoat che do
//     giai thich, cac cong thuc phia sau bi noi len phan cau hoi.
// Nay token duoc chen ngay trong doan van goc, kem <w:br/> khi can de cong thuc
// van nam tren mot dong rieng nhu Word hien thi.
// ============================================================
const replaceMathParaWithToken = (doc, mathParaNode, token) => {
    const parent = mathParaNode.parentNode;
    if (!parent) {
        return;
    }

    const paragraph = findWordParagraphAncestor(mathParaNode);
    if (!paragraph) {
        parent.replaceChild(createWordParagraph(doc, token), mathParaNode);
        return;
    }

    const previous = findAdjacentVisibleNode(mathParaNode, paragraph, true);
    const next = findAdjacentVisibleNode(mathParaNode, paragraph, false);

    if (previous && !isWordLineBreakNode(previous)) {
        parent.insertBefore(createWordLineBreakRun(doc), mathParaNode);
    }

    parent.insertBefore(createWordTextRun(doc, token), mathParaNode);

    if (next && !isWordLineBreakNode(next)) {
        parent.insertBefore(createWordLineBreakRun(doc), mathParaNode);
    }

    parent.removeChild(mathParaNode);
};

const buildDocxMathToken = (index) => `${DOCX_MATH_TOKEN_PREFIX}${index}__`;

const injectMathMlAttributes = (mathmlString, isBlock) => {
    const input = getTrimmedString(mathmlString);
    if (!input.startsWith('<math')) {
        return input;
    }

    const displayValue = isBlock ? 'block' : 'inline';
    return input.replace(/<math\b([^>]*)>/i, (match, attrs) => {
        let nextAttrs = attrs || '';

        if (/\bdata-docx-block\s*=/.test(nextAttrs)) {
            nextAttrs = nextAttrs.replace(/\bdata-docx-block\s*=\s*(['"])[^'"]*\1/i, `data-docx-block="${isBlock ? 'true' : 'false'}"`);
        } else {
            nextAttrs += ` data-docx-block="${isBlock ? 'true' : 'false'}"`;
        }

        if (/\bdisplay\s*=/.test(nextAttrs)) {
            nextAttrs = nextAttrs.replace(/\bdisplay\s*=\s*(['"])[^'"]*\1/i, `display="${displayValue}"`);
        } else {
            nextAttrs += ` display="${displayValue}"`;
        }

        return `<math${nextAttrs}>`;
    });
};

// Khop moi token cong thuc da chen vao document.xml, vd "DOCX_MATH_TOKEN_12__".
const DOCX_MATH_TOKEN_PATTERN = new RegExp(
    `(?:${DOCX_MATH_TOKEN_PREFIX}|${DOCX_IMAGE_TOKEN_PREFIX})\\d+__`,
    'g'
);

const buildDocxMathTokenMap = (placeholders) => {
    const tokenMap = new Map();
    (Array.isArray(placeholders) ? placeholders : []).forEach((placeholder) => {
        tokenMap.set(placeholder.token, placeholder);
    });

    return tokenMap;
};

// Thay token cong thuc bang LaTeX/MathML tuong ung.
// Truoc day ham nay split/join CHUOI DAY DU cho TUNG placeholder, nen chi phi la
// O(so_block x so_cong_thuc x do_dai_chuoi) — de nhieu cong thuc + anh base64 se
// rat cham. Nay chi duyet moi chuoi dung 1 lan bang regex.
const replaceDocxMathTokens = (input, placeholders, mapper) => {
    const source = String(input || '');

    // Loi tat cho truong hop pho bien nhat: dong khong chua token nao.
    if (!source || !source.includes('_TOKEN_')) {
        return source;
    }

    const tokenMap = placeholders instanceof Map ? placeholders : buildDocxMathTokenMap(placeholders);
    if (tokenMap.size === 0) {
        return source;
    }

    return source.replace(DOCX_MATH_TOKEN_PATTERN, (token) => {
        const placeholder = tokenMap.get(token);
        return placeholder ? mapper(placeholder) : token;
    });
};

const replaceDocxMathTokensInBlocks = (blocks, placeholders) => {
    if (!Array.isArray(blocks) || blocks.length === 0 || placeholders.length === 0) {
        return blocks;
    }

    // Dung bang tra cuu 1 lan cho toan bo block thay vi dung lai cho tung block.
    const tokenMap = buildDocxMathTokenMap(placeholders);

    return blocks.map((block) => ({
        ...block,
        text: replaceDocxMathTokens(block.text, tokenMap, (placeholder) => placeholder.textReplacement),
        html: replaceDocxMathTokens(block.html, tokenMap, (placeholder) => placeholder.htmlReplacement),
    }));
};

// Chuan hoa LaTeX sinh ra tu OMML de giam loi parse o layer render.
const normalizeOmmlLatex = (value) => {
    let normalized = getTrimmedString(value);

    if (!normalized) {
        return '';
    }

    normalized = normalized.replace(/\\hfill/g, ' ');
    // Chuyen cac macro amsmath de KaTeX an toan hon.
    normalized = normalized.replace(/\\hdots\b/g, '\\cdots');
    normalized = normalized.replace(/\\dots(?:b|c|i|m|o)\b/g, '\\dots');
    normalized = normalized.replace(/\\matrix\s*\{/g, '\\begin{matrix}');
    normalized = normalized.replace(/\\endmatrix\b/g, '\\end{matrix}');

    // Word/convert thuong mat ky tu escape voi dau ngoac nhon cua \left/\right.
    normalized = normalized.replace(/\\left\s*\{/g, '\\left\\{');
    normalized = normalized.replace(/\\right\s*\}/g, '\\right\\}');
    normalized = normalized.replace(/\\\{\s*\\begin\{matrix\}/g, '\\left\\{\\begin{matrix}');

    // DOCX hay nhan doi delimiter khi cong thuc co dau gia tri |_{a}^{b}.
    normalized = normalized.replace(/\\left\s*\(\s*\\left\s*\(/g, '\\left(');
    normalized = normalized.replace(/\\left\s*\[\s*\\left\s*\[/g, '\\left[');
    normalized = normalized.replace(/\\left\s*\\\{\s*\\left\s*\\\{/g, '\\left\\{');
    normalized = normalized.replace(/\\right\s*\)\s*\\right\s*\)/g, '\\right)');
    normalized = normalized.replace(/\\right\s*\]\s*\\right\s*\]/g, '\\right]');
    normalized = normalized.replace(/\\right\s*\\\}\s*\\right\s*\\\}/g, '\\right\\}');

    // Chuyen dang DOCX "\left(...\right)\right|_{a}^{b}" ve dang KaTeX co the parse.
    normalized = normalized.replace(
        /\\left\s*\(([\s\S]*?)\\right\s*\)\s*\\right\s*(?:\||\\vert|∣)\s*_(\{[^}]*\}|[^\s^]+)\s*\^(\{[^}]*\}|[^\s]+)(?=\s*$)/,
        (match, inner, lower, upper) => `\\left. {\\left(${inner}\\right)} \\right|_${lower}^${upper}`
    );

    if (/\\left\\\{\\begin\{matrix\}/.test(normalized) && /\\end\{matrix\}/.test(normalized) && !/\\right\./.test(normalized) && !/\\right\\\}/.test(normalized)) {
        normalized = normalized.replace(/\\end\{matrix\}/, '\\end{matrix}\\right.');
    }

    // Sua loi convert bi lap token mo/dong ngoac tu dong.
    normalized = normalized.replace(/(?:\\left\s*){2,}(?=\\?[({])/g, '\\left');
    normalized = normalized.replace(/(?:\\right\s*\.?\s*){2,}/g, '\\right.');
    normalized = normalized.replace(/\\right\s*$/g, '\\right.');

    return normalized;
};

const convertOmmlElementToMathPayload = (ommlElement, isBlock = false) => {
    let mathml = '';

    try {
        const mathmlElement = omml2mathml(ommlElement.cloneNode(true));
        const mathmlString = new XMLSerializer().serializeToString(mathmlElement);
        mathml = injectMathMlAttributes(mathmlString, isBlock);
        const latex = normalizeOmmlLatex(MathMLToLaTeX.convert(mathmlString));

        return {
            latex,
            mathml,
        };
    } catch (error) {
        // Fall through to text fallback below.
    }

    return {
        latex: normalizeOmmlLatex(ommlElement.textContent),
        mathml,
    };
};

// ============================================================
// ĐỌC BẢNG QUAN HỆ word/_rels/document.xml.rels
// Cần để tra r:id -> đường dẫn file ảnh trong gói DOCX.
// ============================================================
const readDocxRelationships = async (zip) => {
    const relationships = new Map();
    const relsFile = zip.file('word/_rels/document.xml.rels');

    if (!relsFile) {
        return relationships;
    }

    const relsXml = await relsFile.async('text');
    const relsDoc = new DOMParser().parseFromString(relsXml, 'application/xml');

    Array.from(relsDoc.getElementsByTagName('Relationship')).forEach((node) => {
        const id = node.getAttribute('Id');
        const target = node.getAttribute('Target');
        if (id && target) {
            relationships.set(id, target);
        }
    });

    return relationships;
};

// ============================================================
// ĐƯA ẢNH KIỂU VML (<w:pict>) VỀ ĐÚNG VỊ TRÍ TRONG DÒNG
// mammoth cố ý đẩy ảnh trong <w:pict> ra NGOÀI đoạn văn:
//     "w:pict": function (element) { return readChildElements(element).toExtra(); }
// Hậu quả: đề dùng công thức/ký hiệu chèn dạng ảnh cũ (rất phổ biến với file
// soạn bằng Equation của Word đời cũ) sẽ có toàn bộ ảnh dồn xuống cuối, tách rời
// khỏi đáp án — người làm bài thấy một cột ảnh trống trơn bên dưới.
// Cách xử lý: thay mỗi <w:pict> bằng một token text NGAY TẠI CHỖ, tự đọc ảnh từ
// gói DOCX rồi trả <img> về đúng vị trí token sau khi mammoth convert xong.
// ============================================================
// ============================================================
// LIỆT KÊ <w:pict> KÈM TRẠNG THÁI FIELD CHỨA NÓ
// Ký hiệu chèn bằng field QUOTE của Word có ĐÚNG HAI bản ảnh giống nhau:
//     fldChar begin -> instrText " QUOTE " -> PICT (phần lệnh)
//     -> fldChar separate -> PICT (phần kết quả) -> fldChar end
// Word chỉ hiển thị bản kết quả. Nếu lấy cả hai thì mỗi ký hiệu bị nhân đôi.
// Hàm này duyệt theo đúng thứ tự tài liệu để biết mỗi <w:pict> nằm ở phần lệnh
// hay phần kết quả, và field đó có bản kết quả hay không.
// ============================================================
const collectPictNodesWithFieldState = (xmlDoc) => {
    const picts = [];
    const fieldStack = [];

    const visit = (node) => {
        if (node.nodeType !== ELEMENT_NODE_TYPE) {
            return;
        }

        if (node.namespaceURI === DOCX_W_NS) {
            if (node.localName === 'fldChar') {
                const type = node.getAttributeNS(DOCX_W_NS, 'fldCharType') || node.getAttribute('w:fldCharType');

                if (type === 'begin') {
                    fieldStack.push({ inInstruction: true, hasResultPict: false });
                } else if (type === 'separate' && fieldStack.length > 0) {
                    fieldStack[fieldStack.length - 1].inInstruction = false;
                } else if (type === 'end') {
                    fieldStack.pop();
                }

                return;
            }

            if (node.localName === 'pict') {
                const field = fieldStack.length > 0 ? fieldStack[fieldStack.length - 1] : null;
                const inInstruction = Boolean(field && field.inInstruction);

                if (field && !inInstruction) {
                    field.hasResultPict = true;
                }

                picts.push({ node, field, inInstruction });
                return;
            }
        }

        Array.from(node.childNodes).forEach(visit);
    };

    visit(xmlDoc.documentElement);
    return picts;
};

const replacePictImagesWithTokens = async (zip, xmlDoc, placeholders, startIndex) => {
    const pictEntries = collectPictNodesWithFieldState(xmlDoc);
    const images = [];

    if (pictEntries.length === 0) {
        return { images, nextIndex: startIndex };
    }

    const relationships = await readDocxRelationships(zip);
    let placeholderIndex = startIndex;

    for (let i = 0; i < pictEntries.length; i += 1) {
        const { node, field, inInstruction } = pictEntries[i];

        // Ban nam trong phan lenh cua field (khi field da co ban ket qua) chinh la
        // ban Word khong hien thi -> bo han, neu khong ky hieu se bi nhan doi.
        if (inInstruction && field && field.hasResultPict) {
            node.parentNode?.removeChild(node);
            continue;
        }

        const imageData = node.getElementsByTagNameNS(VML_NS, 'imagedata')[0];

        if (!imageData || !node.parentNode) {
            continue;
        }

        const relationshipId = imageData.getAttributeNS(DOCX_R_NS, 'id') || imageData.getAttribute('r:id');
        const target = relationshipId ? relationships.get(relationshipId) : '';

        if (!target) {
            continue;
        }

        const entryPath = `word/${String(target).replace(/^\/+/, '')}`;
        const imageFile = zip.file(entryPath);

        if (!imageFile) {
            continue;
        }

        // eslint-disable-next-line no-await-in-loop
        const base64 = await imageFile.async('base64');
        if (!base64) {
            continue;
        }

        const contentType = getImageContentType(entryPath);
        const imageNumber = images.length + 1;
        const token = `${DOCX_IMAGE_TOKEN_PREFIX}${placeholderIndex}__`;
        placeholderIndex += 1;
        // Giu kich thuoc Word hien thi (style cua <v:shape>), khong theo so pixel cua file anh.
        const sizeStyle = buildImageSizeStyle(readVmlShapeImageSize(imageData.parentNode));

        placeholders.push({
            token,
            // Anh khong sinh ra chu nao trong ban text thuan.
            textReplacement: '',
            htmlReplacement: `<img src="data:${contentType};base64,${base64}" data-docx-image-index="${imageNumber}"`
                + `${sizeStyle ? ` style="${sizeStyle}"` : ''} />`,
        });
        images.push({ imageNumber, contentType });

        node.parentNode.replaceChild(createWordText(xmlDoc, token), node);
    }

    return { images, nextIndex: placeholderIndex };
};

const patchDocxOmmlIntoLatex = async (arrayBuffer) => {
    if (!arrayBuffer || typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
        return {
            arrayBuffer,
            placeholders: [],
            pictImages: [],
            imageSizes: new Map(),
        };
    }

    try {
        const zip = await JSZip.loadAsync(arrayBuffer);
        const documentFile = zip.file('word/document.xml');
        const placeholders = [];
        let placeholderIndex = 0;

        if (!documentFile) {
            return {
                arrayBuffer,
                placeholders,
                pictImages: [],
                imageSizes: new Map(),
            };
        }

        const xmlText = await documentFile.async('text');
        const xmlDoc = new DOMParser().parseFromString(xmlText, 'application/xml');

        const parserError = xmlDoc.getElementsByTagName('parsererror')[0];
        if (parserError) {
            return {
                arrayBuffer,
                placeholders,
                pictImages: [],
                imageSizes: new Map(),
            };
        }

        const mathParaNodes = Array.from(xmlDoc.getElementsByTagNameNS(DOCX_M_NS, 'oMathPara'));
        mathParaNodes.forEach((node) => {
            const payload = convertOmmlElementToMathPayload(node, true);
            const latex = payload.latex;

            if (!latex) {
                return;
            }

            const token = buildDocxMathToken(placeholderIndex);
            placeholderIndex += 1;
            placeholders.push({
                token,
                textReplacement: `$${latex}$`,
                htmlReplacement: payload.mathml || escapeHtml(`$${latex}$`),
            });

            replaceMathParaWithToken(xmlDoc, node, token);
        });

        const mathNodes = Array.from(xmlDoc.getElementsByTagNameNS(DOCX_M_NS, 'oMath'));
        mathNodes.forEach((node) => {
            if (!node.parentNode || isDescendantOfMathPara(node)) {
                return;
            }

            const payload = convertOmmlElementToMathPayload(node, false);
            const latex = payload.latex;

            if (!latex) {
                return;
            }

            const token = buildDocxMathToken(placeholderIndex);
            placeholderIndex += 1;
            placeholders.push({
                token,
                textReplacement: `$${latex}$`,
                htmlReplacement: payload.mathml || escapeHtml(`$${latex}$`),
            });

            const replacement = createWordTextRun(xmlDoc, token);
            node.parentNode?.replaceChild(replacement, node);
        });

        // Anh kieu <w:pict> phai duoc thay bang token TRUOC khi giao cho mammoth,
        // neu khong mammoth se day chung ra ngoai doan van.
        const pictResult = await replacePictImagesWithTokens(zip, xmlDoc, placeholders, placeholderIndex);
        placeholderIndex = pictResult.nextIndex;

        // Chay SAU buoc tren: anh <w:pict> da tu mang kich thuoc trong token cua no.
        const imageSizes = tagImagesWithWordSize(xmlDoc);

        zip.file('word/document.xml', new XMLSerializer().serializeToString(xmlDoc));
        return {
            arrayBuffer: await zip.generateAsync({ type: 'arraybuffer' }),
            placeholders,
            pictImages: pictResult.images,
            imageSizes,
        };
    } catch (error) {
        return {
            arrayBuffer,
            placeholders: [],
            pictImages: [],
            imageSizes: new Map(),
        };
    }
};

// Khớp dòng bắt đầu câu hỏi theo nhiều biến thể thường gặp trong đề DOCX.
const matchQuestionStart = (line) => {
    const input = normalizeParserLine(line);

    for (const pattern of questionStartPatterns) {
        const match = input.match(pattern);
        if (match) {
            return {
                questionNumber: Number(match[1]) || null,
                questionText: getTrimmedString(match[2]),
                questionNumberSource: 'explicit',
            };
        }
    }

    return null;
};

const instructionLineRegex = /^(?:mark\s+the\s+letter|read\s+the\s+following|choose\s+the\s+best|complete\s+the\s+sentence|fill\s+in\s+the\s+blank|write\s+(?:a|an|the))/i;

const matchAutoNumberedQuestionStart = (line, current) => {
    const input = normalizeParserLine(line);
    const match = input.match(numberedQuestionStartRegex);

    if (!match) {
        return null;
    }

    const questionNumber = Number(match[1]) || null;
    const questionText = getTrimmedString(match[2]);

    if (!questionText) {
        return null;
    }

    // Truong hop DOCX dung nhan "Cau n" ro rang, Word co the tu dong doi list A/B/C/D
    // thanh 1/2/3/4. Khi dang parse option cua cau hien tai, 1-4 khong duoc coi la cau moi.
    if (
        current
        && current.questionNumberSource === 'explicit'
        && current.optionOrder.length > 0
        && questionNumber >= 1
        && questionNumber <= ANSWER_LETTERS.length
    ) {
        return null;
    }

    // Tranh nham cac dong huong dan mo dau section thanh cau hoi.
    if (!current && instructionLineRegex.test(questionText)) {
        return null;
    }

    if (current) {
        const hasCollectedOptions = current.optionOrder.length > 0;
        const hasTextAnswerConfig = current.sampleAnswers.length > 0 || current.questionType === 'text';
        const hasExplicitCorrect = current.explicitCorrectLetters.length > 0 || current.starredCorrectLetters.length > 0;
        const canSafelySplit = hasCollectedOptions || hasTextAnswerConfig || hasExplicitCorrect || current.mode === 'explanation';

        // Neu dang o phan than cau hoi (chua co option) thi 1.,2.,3. kha nang cao la sub-item.
        if (!canSafelySplit) {
            return null;
        }
    }

    return {
        questionNumber,
        questionText,
        questionNumberSource: 'auto-numbered',
    };
};

// Escape ky tu HTML dac biet khi can render text thuan an toan.
const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Noi them mot doan HTML vao cuoi chuoi hien tai, cach nhau bang <br/>.
const appendHtml = (current, fragment) => {
    // Loai bo khoang trang du thua va bo qua fragment rong.
    const next = getTrimmedString(fragment);
    if (!next) {
        return current;
    }
    // Neu chua co noi dung truoc do thi dung luon fragment moi.
    if (!current) {
        return next;
    }

    // Bang (<table>) la phan tu block-level, tu no da xuong dong roi,
    // nen khong can chen them <br/> truoc/sau bang de tranh khoang trang thua.
    const currentEndsWithTable = /<\/table>\s*$/i.test(current);
    const nextStartsWithTable = /^<table\b/i.test(next);
    if (currentEndsWithTable || nextStartsWithTable) {
        return `${current}${next}`;
    }

    return `${current}<br/>${next}`;
};

// Noi dong text thuong cho cac khoi can giu xuong dong (section/passage).
const appendTextLine = (current, line) => {
    const next = getTrimmedString(line);
    if (!next) {
        return current;
    }
    if (!current) {
        return next;
    }
    return `${current}\n${next}`;
};

const splitHtmlByLineBreaks = (html) => String(html || '')
    .split(/<br\s*\/?>/i)
    .map((part) => getTrimmedString(part));

// ============================================================
// CONTAINER DOM TAM DUNG CHUNG
// parseQuizFromBlocks goi htmlToPlainText / stripLeadingTextFromHtml cho TUNG
// dong cua tai lieu, ma moi lan lai `new DOMParser().parseFromString(...)` —
// tao han mot document moi chi de doc vai chuc ky tu. Tai su dung container
// theo tung muc dich (key) giup bo chi phi nay o de dai hang nghin dong.
// Document sinh boi DOMParser la document "tro", script trong HTML khong chay.
// ============================================================
const htmlScratchContainers = new Map();

const getHtmlScratchContainer = (key) => {
    if (typeof DOMParser === 'undefined') {
        return null;
    }

    if (htmlScratchContainers.has(key)) {
        return htmlScratchContainers.get(key);
    }

    const doc = new DOMParser().parseFromString('<div></div>', 'text/html');
    const container = doc.body.firstElementChild;
    htmlScratchContainers.set(key, container);

    return container;
};

const htmlToPlainText = (html) => {
    const input = getTrimmedString(html);
    if (!input) {
        return '';
    }

    const container = getHtmlScratchContainer('plain-text');
    if (!container) {
        return input.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    container.innerHTML = input;

    // Chen dau phan cach cho bang de cac o khong dinh lien nhau thanh 1 chuoi.
    // Phai chen VAO DOM sau khi parse: neu chen vao chuoi HTML tho (cach cu) thi
    // text nam giua cac the <td> se bi trinh phan tich HTML day ra ngoai bang
    // ("foster parenting"), nen dau phan cach khong bao gio nam dung cho.
    container.querySelectorAll('tr').forEach((row) => {
        const cells = Array.from(row.children).filter((cell) => cell.tagName === 'TD' || cell.tagName === 'TH');

        cells.forEach((cell, index) => {
            if (index < cells.length - 1) {
                cell.appendChild(cell.ownerDocument.createTextNode(' | '));
            }
        });

        row.appendChild(row.ownerDocument.createTextNode('\n'));
    });

    return getTrimmedString(container.textContent || '').replace(/[ \t\f\v]+/g, ' ');
};

const toAlphaLabel = (indexNumber) => {
    const value = Number(indexNumber);
    if (!Number.isFinite(value) || value <= 0) {
        return '';
    }

    let num = Math.floor(value);
    let label = '';
    while (num > 0) {
        const rem = (num - 1) % 26;
        label = String.fromCharCode(97 + rem) + label;
        num = Math.floor((num - 1) / 26);
    }

    return label;
};

// Nhan dien tieu de lon dang "Phan 1" / "Part 2" / "Part II".
const parseSectionHeaderLine = (line) => {
    const input = getTrimmedString(line);
    if (!input) {
        return null;
    }

    const match = input.match(sectionHeaderRegex);
    if (!match) {
        return null;
    }

    const sectionCore = getTrimmedString(match[1]);
    const trailing = getTrimmedString(match[3]);

    // Truong hop tieu de section va cau hoi dau tien nam tren cung 1 dong.
    if (trailing && matchQuestionStart(trailing)) {
        return {
            title: sectionCore,
            trailingText: trailing,
        };
    }

    return {
        // Giu nguyen full line khi trailing la mo ta section (khong phai cau hoi).
        title: input,
        trailingText: '',
    };
};

const createSection = (sectionId, title, titleHtml) => ({
    id: sectionId,
    title: getTrimmedString(title),
    titleHtml: getTrimmedString(titleHtml),
    content: '',
    contentHtml: '',
});

const decodeHtmlEntity = (entity) => {
    const lower = String(entity || '').toLowerCase();
    if (lower === '&nbsp;') {
        return ' ';
    }
    if (lower === '&amp;') {
        return '&';
    }
    if (lower === '&lt;') {
        return '<';
    }
    if (lower === '&gt;') {
        return '>';
    }
    if (lower === '&quot;') {
        return '"';
    }
    if (lower === '&#39;' || lower === '&apos;') {
        return "'";
    }

    const numericMatch = lower.match(/^&#(\d+);$/);
    if (numericMatch) {
        const codePoint = Number(numericMatch[1]);
        return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : '';
    }

    const hexMatch = lower.match(/^&#x([0-9a-f]+);$/);
    if (hexMatch) {
        const codePoint = Number.parseInt(hexMatch[1], 16);
        return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : '';
    }

    return '';
};

// Tao mapping tu plain text sang offset chuoi HTML de cat dung fragment giu markup.
const buildHtmlPlainIndexMap = (html) => {
    const source = String(html || '');
    const plainChars = [];
    const plainToHtml = [];

    let index = 0;
    while (index < source.length) {
        const char = source[index];

        if (char === '<') {
            const closeIndex = source.indexOf('>', index + 1);
            if (closeIndex === -1) {
                break;
            }
            index = closeIndex + 1;
            continue;
        }

        if (char === '&') {
            const semiIndex = source.indexOf(';', index + 1);
            if (semiIndex !== -1) {
                const entity = source.slice(index, semiIndex + 1);
                const decoded = decodeHtmlEntity(entity);

                if (decoded) {
                    plainToHtml.push(index);
                    plainChars.push(decoded);
                    index = semiIndex + 1;
                    continue;
                }
            }
        }

        plainToHtml.push(index);
        plainChars.push(char);
        index += 1;
    }

    return {
        html: source,
        plain: plainChars.join(''),
        plainToHtml,
    };
};

const getHtmlOffsetFromPlainIndex = (mapping, plainIndex) => {
    if (!mapping) {
        return 0;
    }

    const index = Number(plainIndex);
    if (!Number.isFinite(index) || index <= 0) {
        return 0;
    }

    if (index >= mapping.plain.length) {
        return mapping.html.length;
    }

    return mapping.plainToHtml[index] ?? mapping.html.length;
};

// ============================================================
// ĐO "ĐỘ MẠNH BIÊN" CỦA MỘT ỨNG VIÊN MARKER ĐÁP ÁN
// Quyết định cụm "A." / "*B)" là marker mở đầu một lựa chọn thật, hay chỉ là
// chữ cái nằm trong nội dung (vd "Vitamin D.", "Hình B."). Càng nhiều dấu hiệu
// ngắt đoạn ở phía trước thì điểm càng cao:
//   4 - đầu chuỗi / sau xuống dòng / sau tab / có dấu '*' đánh dấu đáp án đúng
//   3 - cách phần trước bởi >= 2 khoảng trắng (kiểu căn cột của Word)
//   2 - ngay trước là dấu kết câu (. ! ? ; : ) ])
//   1 - chỉ cách bởi 1 khoảng trắng (biên yếu, vd "Hà Nội B. Huế")
//   0 - dính liền chữ/số hoặc đứng sau '(' -> chắc chắn KHÔNG phải marker
// Điểm này KHÔNG dùng để loại từng marker riêng lẻ (cách cũ đã bị comment out
// vì loại nhầm cả "*C." lẫn "Sắt D. Kẽm"), mà dùng để chấm điểm cả dãy marker
// trong selectBestMarkerChain bên dưới.
// ============================================================
const getMarkerBoundaryStrength = (input, letterIndex) => {
    let scanIndex = letterIndex - 1;
    let spaceCount = 0;
    let hasStar = false;

    // Lùi qua cụm khoảng trắng và dấu '*' để tìm ký tự "neo" thực sự phía trước.
    while (scanIndex >= 0 && (input[scanIndex] === ' ' || input[scanIndex] === '*')) {
        if (input[scanIndex] === '*') {
            hasStar = true;
        } else {
            spaceCount += 1;
        }
        scanIndex -= 1;
    }

    const anchor = scanIndex >= 0 ? input[scanIndex] : '';

    if (anchor === '' || anchor === '\n' || anchor === '\t') {
        return 4;
    }
    if (hasStar) {
        return 4;
    }
    // Dính liền ký tự chữ/số (vd "ABCD.") hoặc dạng "(A)" -> không phải marker.
    if (spaceCount === 0 && (anchor === '(' || /[A-Za-z0-9'’]/.test(anchor))) {
        return 0;
    }
    if (spaceCount >= 2) {
        return 3;
    }
    if (/[.!?;:)\]]/.test(anchor)) {
        return 2;
    }

    return 1;
};

// So sánh 2 dãy marker, trả về true nếu "next" tốt hơn "best".
// Thứ tự ưu tiên: dãy dài hơn > ít lựa chọn rỗng hơn > tổng độ mạnh biên cao hơn.
const isBetterMarkerChain = (next, best) => {
    if (!best) {
        return true;
    }
    if (next.markers.length !== best.markers.length) {
        return next.markers.length > best.markers.length;
    }
    if (next.emptyCount !== best.emptyCount) {
        return next.emptyCount < best.emptyCount;
    }

    return next.strength > best.strength;
};

// ============================================================
// CHỌN DÃY MARKER HỢP LÝ NHẤT TRÊN MỘT DÒNG
// Marker thật của một dòng đáp án inline luôn là dãy chữ cái LIÊN TIẾP theo
// đúng thứ tự bảng chữ cái (A -> B -> C -> D) với vị trí tăng dần. Vì vậy thay
// vì phán đoán từng marker rời rạc, hàm này duyệt mọi tổ hợp ứng viên và chọn
// dãy có điểm tốt nhất.
// Nhờ đó "A. Vitamin D. B. Vitamin C. *C. Sắt D. Kẽm" tách đúng thành 4 lựa
// chọn (chữ "D."/"C." trong nội dung bị bỏ qua vì biên yếu hơn marker thật).
// ============================================================
const selectBestMarkerChain = (input, candidates) => {
    if (candidates.length === 0) {
        return [];
    }

    // cache[startIdx] = dãy tốt nhất bắt đầu tại candidates[startIdx].
    const cache = new Map();

    const buildFrom = (startIdx) => {
        if (cache.has(startIdx)) {
            return cache.get(startIdx);
        }

        const candidate = candidates[startIdx];
        const nextLetter = ANSWER_LETTERS[ANSWER_LETTERS.indexOf(candidate.letter) + 1];

        // Mặc định: dãy chỉ gồm marker này, nội dung chạy tới hết dòng.
        let best = {
            markers: [candidate],
            strength: candidate.strength,
            emptyCount: getTrimmedString(input.slice(candidate.end)) ? 0 : 1,
        };
        // Ghi cache sớm để phòng trường hợp dữ liệu bất thường gây đệ quy lặp.
        cache.set(startIdx, best);

        if (nextLetter) {
            for (let nextIdx = startIdx + 1; nextIdx < candidates.length; nextIdx += 1) {
                const next = candidates[nextIdx];
                if (next.letter !== nextLetter || next.index < candidate.end) {
                    continue;
                }

                const tail = buildFrom(nextIdx);
                const isEmpty = getTrimmedString(input.slice(candidate.end, next.index)) === '';
                const chain = {
                    markers: [candidate, ...tail.markers],
                    strength: candidate.strength + tail.strength,
                    emptyCount: tail.emptyCount + (isEmpty ? 1 : 0),
                };

                if (isBetterMarkerChain(chain, best)) {
                    best = chain;
                }
            }
        }

        cache.set(startIdx, best);
        return best;
    };

    let bestChain = null;
    for (let index = 0; index < candidates.length; index += 1) {
        const chain = buildFrom(index);
        // Ứng viên được duyệt theo vị trí tăng dần nên khi điểm bằng nhau,
        // dãy bắt đầu sớm hơn (đã gán trước) được giữ lại.
        if (isBetterMarkerChain(chain, bestChain)) {
            bestChain = chain;
        }
    }

    return bestChain ? bestChain.markers : [];
};

const findInlineOptionMarkers = (input) => {
    const normalized = String(input || '');
    if (!normalized) {
        return [];
    }

    // Lấy danh sách các vùng chứa công thức toán để loại trừ sau này
    const mathRanges = getMathRanges(normalized);

    // Regex bắt pattern: [*] + [khoảng trắng] + [1 chữ cái A-D] + [khoảng trắng] + [. hoặc )]
    const markerRegex = /\*?\s*([A-D])\s*[.)]/g;
    const candidates = [];
    let match = markerRegex.exec(normalized);

    while (match) {
        const markerText = match[0] || '';

        // Tìm vị trí chính xác của chữ cái A-D bên trong chuỗi match
        // (vì match có thể bao gồm cả dấu '*' và khoảng trắng phía trước)
        const letterOffset = markerText.search(/[A-D]/);
        const letterIndex = letterOffset >= 0 ? match.index + letterOffset : match.index;

        // Marker nằm trong công thức toán -> bỏ qua, không phải marker thật.
        if (isIndexInMathRange(letterIndex, mathRanges)) {
            match = markerRegex.exec(normalized);
            continue;
        }

        const strength = getMarkerBoundaryStrength(normalized, letterIndex);

        // Độ mạnh 0 = chữ cái dính liền từ khác hoặc dạng "(A)" -> loại thẳng.
        if (strength === 0) {
            match = markerRegex.exec(normalized);
            continue;
        }

        candidates.push({
            index: match.index,        // vị trí bắt đầu match (bao gồm cả '*' nếu có)
            letterIndex,                // vị trí chính xác của chữ cái A-D
            end: markerRegex.lastIndex, // vị trí kết thúc match (sau dấu . hoặc ))
            letter: match[1].toUpperCase(),
            isCorrect: markerText.includes('*'), // có dấu '*' => đây là đáp án đúng
            strength,
        });

        match = markerRegex.exec(normalized);
    }

    // Lọc lần cuối: chỉ giữ dãy marker liên tiếp hợp lý nhất.
    return selectBestMarkerChain(normalized, candidates);
};

// ============================================================
// PHÁT HIỆN MARKER GIẢ Ở CUỐI DÒNG
// Một chữ cái A-D + dấu chấm nằm ở CUỐI dòng (ví dụ "B. Thêm một cặp G - C.")
// rất dễ bị đọc nhầm thành marker mở đầu một lựa chọn mới. Lựa chọn "ma" này
// không có nội dung nào phía sau, và nó nuốt mất phần đuôi của lựa chọn trước.
// Đây chính là lỗi làm "B. Thêm một cặp G - C." bị cắt còn "Thêm một cặp G -"
// ở các đề trình bày 2 đáp án trên một dòng.
//
// Trả về chỉ số của lựa chọn cuối cùng CÒN GIỮ LẠI, hoặc null nếu không có
// marker giả. Không cắt bỏ khi:
//   - cắt xong còn dưới 2 lựa chọn (khi đó không còn là dòng đáp án inline)
//   - lựa chọn cuối tuy rỗng text nhưng HTML có ảnh/bảng -> là lựa chọn thật
//   - mọi lựa chọn đều rỗng text (dòng toàn ảnh) -> không phải marker giả
// ============================================================
const findLastRealOptionIndex = (options) => {
    let lastIndex = options.length - 1;

    while (
        lastIndex >= 2
        && options[lastIndex].text === ''
        && !hasVisibleHtmlContent(options[lastIndex].html)
    ) {
        lastIndex -= 1;
    }

    if (lastIndex === options.length - 1) {
        return null;
    }

    // Dòng toàn ảnh: không lựa chọn nào có text -> giữ nguyên, không phải marker giả.
    const hasTextOption = options.slice(0, lastIndex + 1).some((option) => option.text !== '');
    if (!hasTextOption) {
        return null;
    }

    return lastIndex;
};

// Cắt HTML theo vị trí ký tự nên hai đầu đoạn có thể hở thẻ
// (ví dụ "</strong>aaBB <img/> aaBb.<strong>"). Cho qua DOM một lượt để đóng thẻ.
const balanceHtmlFragment = (html) => {
    const input = getTrimmedString(html);
    const container = input ? getHtmlScratchContainer('option-html') : null;

    if (!container) {
        return input;
    }

    container.innerHTML = input;
    return getTrimmedString(container.innerHTML);
};

// ============================================================
// DÒNG CHỈ CÓ NHÃN ĐÁP ÁN, KHÔNG CÓ NỘI DUNG NÀO
// Ví dụ "A.  B.  C.  D." — thường gặp khi đáp án là hình vẽ được vẽ bằng công cụ
// Shape/SmartArt của Word (không phải ảnh chèn vào), nên khi chuyển sang HTML thì
// không còn gì cả. Nhận diện riêng để báo đúng nguyên nhân thay vì để parser hiểu
// nhầm cả dòng thành mỗi lựa chọn A.
// ============================================================
const isEmptyInlineOptionRow = (line, lineHtml) => {
    const input = getTrimmedString(line);
    const markers = findInlineOptionMarkers(input);

    if (markers.length < 2) {
        return false;
    }

    const hasAnyContent = markers.some((marker, index) => {
        const end = index < markers.length - 1 ? markers[index + 1].index : input.length;
        return getTrimmedString(input.slice(marker.end, end)) !== '';
    });

    if (hasAnyContent) {
        return false;
    }

    // Không có chữ thì phải chắc chắn cũng không có ảnh/bảng trong HTML.
    // Lưu ý: không dùng hasVisibleHtmlContent ở đây vì chính các nhãn "A. B. C. D."
    // đã là text hiển thị được, sẽ luôn cho kết quả "có nội dung".
    return !/(<img\b|<table\b|<svg\b|<video\b|<audio\b|<figure\b)/i.test(String(lineHtml || ''));
};

// Tách trường hợp câu hỏi và đáp án viết trên cùng 1 dòng (inline options).
// VD: "Câu hỏi? A. đáp án 1 B. đáp án 2 C. đáp án 3 D. đáp án 4"
const extractInlineOptions = (value, htmlValue = '') => {
    const input = getTrimmedString(value);
    if (!input) {
        return null;
    }

    // Bước 1: Tìm tất cả marker A/B/C/D trên bản text thuần (plain text)
    const markers = findInlineOptionMarkers(input);


    if (markers.length < 2) {
        // Ít hơn 2 marker thì không thể coi là có ít nhất 2 đáp án -> không hợp lệ
        return null;
    }

    // Phần text trước marker đầu tiên chính là nội dung câu hỏi
    const questionText = getTrimmedString(input.slice(0, markers[0].index));

    // Cắt nội dung của từng option: từ sau marker hiện tại -> trước marker kế tiếp
    const options = markers
        .map((marker, idx) => {
            const nextIndex = idx < markers.length - 1 ? markers[idx + 1].index : input.length;
            const text = getTrimmedString(input.slice(marker.end, nextIndex));

            return {
                letter: marker.letter,
                isCorrect: marker.isCorrect,
                // Giữ lại độ mạnh biên để nơi gọi quyết định có cảnh báo
                // "viết chung dòng" hay không (biên yếu dễ là dương tính giả).
                strength: marker.strength,
                text,
            };
        });

    const result = {
        questionText,
        questionHtml: '', // sẽ được điền sau nếu có bản HTML tương ứng
        options,
    };

    // Bước cuối dùng chung cho mọi nhánh thoát: bỏ marker giả ở cuối dòng, trả lại
    // phần text/HTML đã bị cắt cho lựa chọn trước đó, rồi loại các lựa chọn rỗng.
    const finalizeResult = (htmlContext) => {
        const lastRealIndex = findLastRealOptionIndex(result.options);

        if (lastRealIndex !== null) {
            const marker = markers[lastRealIndex];
            const extendedOption = {
                ...result.options[lastRealIndex],
                // Nội dung chạy tới hết dòng thay vì dừng ở marker giả.
                text: getTrimmedString(input.slice(marker.end)),
            };

            if (htmlContext) {
                const startOffset = getHtmlOffsetFromPlainIndex(
                    htmlContext.mapping,
                    htmlContext.markers[lastRealIndex].end
                );
                extendedOption.html = getTrimmedString(htmlContext.input.slice(startOffset));
            }

            result.options = [...result.options.slice(0, lastRealIndex), extendedOption];
        }

        // Lọc bỏ các option rỗng còn lại (không có text lẫn HTML hiển thị được)
        const validOptions = result.options.filter(
            (option) => option.text !== '' || hasVisibleHtmlContent(option.html)
        );

        // Nếu sau khi lọc còn lại < 2 option hợp lệ -> không thể coi là câu hỏi inline
        if (validOptions.length < 2) {
            return null;
        }

        result.options = validOptions;
        return result;
    };

    // Nếu không có bản HTML kèm theo -> trả về kết quả chỉ với plain text
    const htmlInput = getTrimmedString(htmlValue);
    if (!htmlInput) {
        return finalizeResult(null);
    }

    // Bước 2: Ánh xạ (map) giữa vị trí trong HTML và vị trí trong plain text
    // để có thể cắt đúng đoạn HTML tương ứng với từng phần nội dung
    const htmlMapping = buildHtmlPlainIndexMap(htmlInput);
    if (!htmlMapping.plain) {
        return finalizeResult(null);
    }

    // Tìm lại marker nhưng trên bản "plain" được trích xuất từ HTML
    const htmlMarkers = findInlineOptionMarkers(htmlMapping.plain);

    // Đối chiếu: số lượng marker giữa 2 bản (text thường vs HTML) phải khớp nhau
    if (htmlMarkers.length < 2 || htmlMarkers.length !== markers.length) {
        return finalizeResult(null); // không khớp -> chỉ dùng phần text, bỏ qua HTML
    }

    // Đối chiếu: thứ tự các chữ cái marker giữa 2 bản phải giống nhau (A,B,C,D...)
    const sameMarkerOrder = htmlMarkers.every((marker, idx) => marker.letter === markers[idx].letter);
    if (!sameMarkerOrder) {
        return finalizeResult(null);
    }

    // Xác định đoạn HTML tương ứng với phần câu hỏi (từ đầu -> trước marker A)
    const questionStart = getHtmlOffsetFromPlainIndex(htmlMapping, 0);
    const questionEnd = getHtmlOffsetFromPlainIndex(htmlMapping, htmlMarkers[0].index);
    result.questionHtml = getTrimmedString(htmlInput.slice(questionStart, questionEnd));

    // Xác định đoạn HTML tương ứng cho từng option, dựa trên vị trí marker đã map
    result.options = result.options.map((option, idx) => {
        const marker = htmlMarkers[idx];
        const nextMarker = idx < htmlMarkers.length - 1 ? htmlMarkers[idx + 1] : null;

        const optionStart = getHtmlOffsetFromPlainIndex(htmlMapping, marker.end);
        const optionEnd = getHtmlOffsetFromPlainIndex(
            htmlMapping,
            // Phải cắt tới letterIndex (vị trí chữ cái) chứ KHÔNG phải index của
            // marker kế tiếp. `index` nằm ngay đầu cụm khoảng trắng, mà thẻ <img>
            // của đáp án bằng hình lại nằm trong chính cụm khoảng trắng đó (ảnh
            // không sinh ra ký tự text nào). Cắt theo `index` sẽ làm mọi đáp án
            // bằng hình trên cùng một dòng bị mất sạch ảnh.
            nextMarker ? nextMarker.letterIndex : htmlMapping.plain.length
        );
        // Cắt tới chữ cái nên có thể dính dấu '*' đánh dấu đáp án đúng của marker
        // kế tiếp ở cuối đoạn -> bỏ đi.
        const optionHtml = balanceHtmlFragment(
            getTrimmedString(htmlInput.slice(optionStart, optionEnd)).replace(/\*\s*$/, '')
        );

        return {
            ...option,
            html: optionHtml, // gán thêm nội dung HTML tương ứng cho option
        };
    });

    return finalizeResult({
        mapping: htmlMapping,
        markers: htmlMarkers,
        input: htmlInput,
    });
};
const toRomanNumeral = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) {
        return '';
    }

    const map = [
        [1000, 'm'],
        [900, 'cm'],
        [500, 'd'],
        [400, 'cd'],
        [100, 'c'],
        [90, 'xc'],
        [50, 'l'],
        [40, 'xl'],
        [10, 'x'],
        [9, 'ix'],
        [5, 'v'],
        [4, 'iv'],
        [1, 'i'],
    ];

    let remaining = Math.floor(num);
    let result = '';
    map.forEach(([unit, symbol]) => {
        while (remaining >= unit) {
            result += symbol;
            remaining -= unit;
        }
    });

    return result;
};

const getOrderedListMarker = (listElement, indexInList) => {
    const startValue = Number.parseInt(listElement?.getAttribute('start') || '1', 10);
    const itemNumber = (Number.isFinite(startValue) ? startValue : 1) + indexInList;
    const rawType = getTrimmedString(listElement?.getAttribute('type')).toLowerCase();
    const styleText = getTrimmedString(listElement?.getAttribute('style')).toLowerCase();

    let listType = rawType;
    if (!listType) {
        if (styleText.includes('lower-alpha')) {
            listType = 'a';
        } else if (styleText.includes('lower-roman')) {
            listType = 'i';
        } else {
            listType = '1';
        }
    }

    if (listType === 'a') {
        const code = 'a'.charCodeAt(0) + itemNumber - 1;
        return `${String.fromCharCode(code)}.`;
    }

    if (listType === 'i') {
        return `${toRomanNumeral(itemNumber)}.`;
    }

    return `${itemNumber}.`;
};

const getListItemPrefix = (node) => {
    if (!node || node.tagName !== 'LI') {
        return '';
    }

    const listElement = node.parentElement;
    if (!listElement) {
        return '';
    }

    if (listElement.tagName === 'OL') {
        const siblings = Array.from(listElement.children).filter((child) => child.tagName === 'LI');
        const indexInList = Math.max(0, siblings.indexOf(node));
        return getOrderedListMarker(listElement, indexInList);
    }

    if (listElement.tagName === 'UL') {
        return '-';
    }

    return '';
};

// Trich cac khoi noi dung tu HTML DOCX de giu text va markup (dam, gach chan, hinh...).
const extractBlocksFromHtml = (html) => {
    if (typeof DOMParser === 'undefined') {
        // Moi truong khong co DOMParser (SSR/Node) thi bo qua nhanh.
        return [];
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nodes = doc.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, td, th, blockquote, pre, img, figure, table');
    const nodeList = Array.from(nodes).filter((node) => {
        // Bang duoc extract nguyen khoi <table>...</table> (1 block duy nhat).
        // Moi node nam BEN TRONG mot table (p, td, th, img... la con chau cua table)
        // deu phai bi loai, neu khong se bi tach thanh block rieng => noi dung lap lai 2 lan.
        // Luu y: mammoth hay boc noi dung moi o bang trong <td><p>...</p></td>,
        // nen chi loai TD/TH la khong du, phai loai ca <p> con ben trong.
        if (node.tagName !== 'TABLE' && node.closest('table')) {
            return false;
        }
        // Mammoth co the sinh ra ca block bao ngoai va <img> con ben trong cho cung 1 anh.
        // Khi do giu ca hai node se lam anh bi render 2 lan, nen chi lay block bao ngoai.
        if (node.tagName === 'IMG' && node.parentElement) {
            const hasExtractedAncestor = Boolean(node.parentElement.closest('p, li, h1, h2, h3, h4, h5, h6, td, th, blockquote, pre, figure, table'));
            if (hasExtractedAncestor) {
                return false;
            }
        }
        // Bang duoc extract nguyen khoi <table>...</table>, khong tach rieng tung td/th nua
        // (neu tach rieng, bang se bi "lam phang" thanh cac dong roi rac, mat het cau truc).
        // if ((node.tagName === 'TD' || node.tagName === 'TH') && node.closest('table')) {
        //     return false;
        // }
        return true;
    });

    return nodeList
        .map((node) => {
            const listItemPrefix = getListItemPrefix(node);
            // Giu ky tu xuong dong tu <br> de parser nhan duoc tung dong trong Word.
            const textNode = node.cloneNode(true);
            textNode.querySelectorAll?.('br').forEach((brNode) => brNode.replaceWith('\n'));
            const rawText = getTrimmedString(textNode.textContent)
                .replace(/[ \t\f\v]+/g, ' ')
                .replace(/\n{3,}/g, '\n\n');
            const text = listItemPrefix && rawText ? `${listItemPrefix} ${rawText}` : rawText;
            const hasMedia = node.matches('img, svg, video, audio, table, figure')
                || Boolean(node.querySelector('img, svg, video, audio, table'));
            // Co media thi uu tien giu outerHTML/innerHTML de khong mat phan tu hinh/ban.
            // const rawHtml = hasMedia && !getTrimmedString(node.innerHTML)
            //     ? getTrimmedString(node.outerHTML)
            //     : getTrimmedString(node.innerHTML);
            // Bang luon giu nguyen the <table> bao ngoai de khong mat cau truc khi render.
            const rawHtml = node.tagName === 'TABLE'
                ? getTrimmedString(node.outerHTML)
                : (hasMedia && !getTrimmedString(node.innerHTML)
                    ? getTrimmedString(node.outerHTML)
                    : getTrimmedString(node.innerHTML));
            const innerHtml = listItemPrefix && rawHtml ? `${escapeHtml(listItemPrefix)} ${rawHtml}` : rawHtml;

            if (!text && !hasMedia) {
                // Node rong thi bo qua de tranh tao dong nhieu nhieu.
                return null;
            }

            return {
                text,
                html: innerHtml,
            };
        })
        .filter(Boolean);
};

// Bo cac ky tu bullet o dau dong de parser de nhan dien mau cau hoi.
const stripLeadingBullets = (line) => line.replace(/^[-+\u2022]\s+/, '').trim();

// Parse "Dap an dung: A, C" thanh mang cac chu cai hop le.
const parseCorrectLetters = (value) => {
    const upper = getTrimmedString(value).toUpperCase();
    if (!upper) {
        return [];
    }

    // Tach theo dau phay va chi giu A-D de tranh ky tu rac.
    return upper
        .split(',')
        .map((part) => part.trim())
        .filter((part) => ANSWER_LETTERS.includes(part));
};

// Parse 1 dong dap an theo mau A./B)/C ... va nhan biet dau * danh dau dap an dung.
const parseOptionLine = (line) => {
    // Ho tro cac mau: *A. ..., A) ..., A ...
    // Khong coi (A) la marker dap an. Sau dau cham/ngoac dong khong duoc la chu cai in hoa,
    // de tranh nham mau hinh hoc nhu D.MNPQ trong de bai.
    const match = line.match(/^(\*)?\s*(?:([A-D])[.:)-](?![A-Z])|([A-D])\s+)\s*(.*)$/);

    // Word co the convert danh sach A/B/C/D thanh 1/2/3/4, map nguoc lai de parse dung.
    const numericMatch = line.match(/^(\*)?\s*(?:([1-4])[.:)-](?![A-Z])|([1-4])\s+)\s*(.*)$/);

    const rawLetter = (match?.[2] || match?.[3] || '').toUpperCase();
    const rawNumeric = (numericMatch?.[2] || numericMatch?.[3] || '').trim();
    const numericLetter = rawNumeric ? ANSWER_LETTERS[Number(rawNumeric) - 1] : '';
    const letter = rawLetter || numericLetter;
    const text = getTrimmedString(match ? match[4] : numericMatch ? numericMatch[4] : '');
    const markerText = match
        ? match[0].slice(0, match[0].length - match[4].length)
        : numericMatch
            ? numericMatch[0].slice(0, numericMatch[0].length - numericMatch[4].length)
            : '';
    const isCorrect = Boolean(match?.[1] === '*' || numericMatch?.[1] === '*');
    // Nhanh thu 2 cua regex (`match[3]`/`numericMatch[3]`) la dang "A ..." khong co
    // dau . : ) - ngan cach. Dang nay de nuot nham mot dong van xuoi trong than cau
    // hoi, nen danh dau lai de nguoi goi canh bao cho nguoi soan de.
    const hasSeparator = Boolean(match ? match[2] : numericMatch?.[2]);

    if (!ANSWER_LETTERS.includes(letter)) {
        // Bao ve them mot lop de loai bo ky tu khong hop le.
        return null;
    }

    return {
        letter,
        isCorrect,
        markerText,
        hasSeparator,
        text,
    };
};

// Rut gon 1 dong dai de nhet vao thong bao canh bao cho de doc.
const summarizeLine = (line, maxLength = 48) => {
    const input = getTrimmedString(line);
    if (input.length <= maxLength) {
        return input;
    }

    return `${input.slice(0, maxLength).trim()}…`;
};

// Dong dai nhieu tu thuong la van xuoi cua de bai chu khong phai mot lua chon ngan.
const looksLikeProseLine = (value) => getTrimmedString(value).split(/\s+/).filter(Boolean).length >= 6;

// ============================================================
// NHAN DIEN NHAN "TRA LOI:" RIENG (mot phan cua explanationRegex)
// "Tra loi: B" trong nhieu de that ra la DAP AN DUNG chu khong phai giai thich,
// nhung parser dang gom nhan nay vao phan giai thich. Tach rieng de canh bao.
// ============================================================
const answerLabelRegex = makeLabelRegex('tra\\s*loi|trả\\s*lời');

// Noi dung chi gom cac ky tu dap an (A / B, D / Dung / Sai...) -> giong khoa dap an.
const isAnswerKeyLikeText = (value) => {
    const input = getTrimmedString(value);
    if (!input || input.length > 24) {
        return false;
    }

    return /^\*?[A-D](?:\s*[,;/+&]?\s*\*?[A-D])*$/i.test(input)
        || /^(?:đúng|dung|sai|true|false)$/i.test(input);
};

// Dem so ky tu A-D dung doc lap trong chuoi, du co phan tach bang dau phay hay khong.
const countStandaloneAnswerLetters = (value) => {
    const matches = getTrimmedString(value)
        .toUpperCase()
        .match(/(?:^|[^A-Z0-9])([A-D])(?![A-Z0-9])/g) || [];

    return new Set(matches.map((item) => item.replace(/[^A-D]/g, ''))).size;
};

// Mot muc trong khoi dap an danh so: "1. ...", "*2) ..."
const numericOptionRunItemRegex = /^\*?\s*(\d+)\s*[.)]\s+\S/;

// ============================================================
// PHAN BIET KHOI "1. / 2. / 3. / 4." LA DAP AN HAY LA CAC Y NHO CUA DE BAI
// Word thuong tu doi danh sach A/B/C/D thanh danh sach so, khi do 1-4 chinh la
// cac lua chon. Nhung de bai cung hay co cac y nho danh so ("Cho cac phat bieu
// sau: 1... 2... 3...") roi moi den dap an A/B/C/D.
// Quy tac phan biet: khoi phai bat dau tu 1 va tang lien tiep; neu NGAY SAU khoi
// van con dong dap an chu cai bat dau bang "A." thi khoi so chi la y nho.
// Tra ve { endIndex, count } neu day la khoi dap an, nguoc lai tra ve null.
// ============================================================
// ============================================================
// DÒNG NGAY SAU KHỐI SỐ CÓ PHẢI LÀ RANH GIỚI KẾT THÚC CÂU HỎI KHÔNG?
// Một bộ đáp án bị Word đổi thành 1/2/3/4 luôn nằm ở CUỐI câu hỏi — ngay sau nó
// phải là câu mới, nhãn ghi chú, tiêu đề phần, hoặc hết tài liệu.
// Ngược lại, nếu phía sau là:
//   - mục số tiếp theo của cùng danh sách (5., 6., ...) -> đây là danh sách ý
//     trong đề bài ("Cho các nhận định dưới đây: 1... 2... 3... 4... 5...")
//   - nhãn "Đáp án mẫu" -> câu trả lời ngắn, các mục số là ý của đề bài
//   - dòng đáp án chữ cái "A. ..." hoặc văn xuôi -> vẫn còn thân câu hỏi
// thì khối số KHÔNG phải là đáp án.
// ============================================================
const isNumericOptionRunBoundary = (nextText, expectedNextNumber) => {
    // Hết tài liệu.
    if (!nextText) {
        return true;
    }

    if (matchQuestionStart(nextText) || sectionHeaderRegex.test(nextText)) {
        return true;
    }

    // Kiem tra truoc cac nhan khac vi "Dap an mau" cung bat dau bang "Dap an".
    if (sampleAnswerRegex.test(nextText)) {
        return false;
    }

    if (
        explanationRegex.test(nextText)
        || hintRegex.test(nextText)
        || correctAnswerRegex.test(nextText)
        || typeRegex.test(nextText)
    ) {
        return true;
    }

    const numericMatch = nextText.match(numericOptionRunItemRegex);
    if (numericMatch) {
        // Danh sach van dem tiep => la danh sach y, khong phai bo dap an.
        return Number(numericMatch[1]) !== expectedNextNumber;
    }

    return false;
};

const detectNumericOptionRun = (lines, startIndex) => {
    let expectedNumber = 1;
    let endIndex = -1;
    let index = startIndex;

    while (index < lines.length && expectedNumber <= ANSWER_LETTERS.length) {
        const text = normalizeParserLine(lines[index]?.text);
        if (!text) {
            // Dong trong xen giua cac lua chon van chap nhan duoc.
            index += 1;
            continue;
        }

        const match = text.match(numericOptionRunItemRegex);
        if (!match || Number(match[1]) !== expectedNumber) {
            break;
        }

        endIndex = index;
        expectedNumber += 1;
        index += 1;
    }

    // Duoi 2 muc thi khong du de coi la mot bo lua chon.
    if (expectedNumber - 1 < 2) {
        return null;
    }

    // Tim dong co noi dung ke tiep de quyet dinh khoi so nay la dap an hay y de bai.
    let nextIndex = index;
    while (nextIndex < lines.length && !normalizeParserLine(lines[nextIndex]?.text)) {
        nextIndex += 1;
    }

    const nextText = nextIndex < lines.length ? normalizeParserLine(lines[nextIndex].text) : '';
    if (!isNumericOptionRunBoundary(nextText, expectedNumber)) {
        return null;
    }

    return {
        endIndex,
        count: expectedNumber - 1,
    };
};

const stripLeadingTextFromHtml = (html, leadingText) => {
    const input = getTrimmedString(html);
    const prefix = String(leadingText || '');
    if (!input || !prefix) {
        return input;
    }

    const container = getHtmlScratchContainer('strip-leading-text');
    if (!container) {
        return input;
    }

    container.innerHTML = input;

    let remaining = prefix;
    let hasMatchedContent = false;

    const consumePrefix = (node) => {
        if (!node || !remaining) {
            return;
        }

        if (node.nodeType === TEXT_NODE_TYPE) {
            let value = node.textContent || '';

            if (!hasMatchedContent) {
                const leadingWhitespace = value.match(/^\s+/);
                if (leadingWhitespace && !remaining.startsWith(leadingWhitespace[0])) {
                    value = value.slice(leadingWhitespace[0].length);
                    node.textContent = value;
                }
            }

            let matchedChars = 0;

            while (
                matchedChars < value.length
                && matchedChars < remaining.length
                && value[matchedChars] === remaining[matchedChars]
            ) {
                matchedChars += 1;
            }

            if (matchedChars > 0) {
                hasMatchedContent = true;
                node.textContent = value.slice(matchedChars);
                remaining = remaining.slice(matchedChars);
            }

            return;
        }

        Array.from(node.childNodes).forEach((childNode) => {
            if (!remaining) {
                return;
            }

            consumePrefix(childNode);

            if (
                childNode.nodeType === ELEMENT_NODE_TYPE
                && childNode.childNodes.length === 0
                && !getTrimmedString(childNode.textContent)
            ) {
                childNode.remove();
            }
        });
    };

    consumePrefix(container);
    return getTrimmedString(container.innerHTML);
};

const stripLeadingLabelFromHtml = (html, fullLineText, contentText) => {
    const lineText = getTrimmedString(fullLineText);
    const content = getTrimmedString(contentText);
    if (!lineText || !content) {
        return getTrimmedString(html);
    }

    const labelPrefix = lineText.slice(0, lineText.indexOf(content));
    if (!labelPrefix) {
        return getTrimmedString(html);
    }

    return stripLeadingTextFromHtml(html, labelPrefix);
};

const hasVisibleHtmlContent = (html) => {
    const input = getTrimmedString(html);
    if (!input) {
        return false;
    }

    // Anh/bang/media duoc xem la co noi dung hop le du text trong option rong.
    if (/(<img\b|<table\b|<svg\b|<video\b|<audio\b|<figure\b)/i.test(input)) {
        return true;
    }

    // Loai bo tag roi kiem tra text con lai.
    const plain = input.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return plain.length > 0;
};

// Tao object cau hoi tam trong khi duyet tung dong cua de.
const createEmptyQuestion = (
    questionNumber,
    questionText,
    section,
    detectedIndex,
    questionNumberSource = 'detected',
    sharedContext = null
) => ({
    detectedIndex,
    questionNumber,
    questionNumberSource,
    questionText: getTrimmedString(questionText),
    questionHtml: getTrimmedString(questionText) ? escapeHtml(questionText) : '',
    sectionId: section?.id || null,
    sectionTitle: getTrimmedString(section?.title),
    sectionTitleHtml: getTrimmedString(section?.titleHtml),
    sectionContent: getTrimmedString(section?.content),
    sectionContentHtml: getTrimmedString(section?.contentHtml),
    sharedContextText: getTrimmedString(sharedContext?.text),
    sharedContextHtml: getTrimmedString(sharedContext?.html),
    explanation: '',
    explanationHtml: '',
    hint: '',
    hintHtml: '',
    optionsByLetter: {},
    optionsByLetterHtml: {},
    optionOrder: [],
    starredCorrectLetters: [],
    explicitCorrectLetters: [],
    questionType: '',
    sampleAnswers: [],
    textInputConfig: {
        caseSensitive: false,
        exactMatch: false,
        placeholder: 'Nhap cau tra loi...',
    },
    subItemCount: 0,
    // Da bao loi "dong dap an rong" cho cau nay chua (tranh bao trung 2 lan).
    emptyOptionRowReported: false,
    mode: 'question',
    currentOptionLetter: null,
});

// Tạo nhãn vị trí câu hỏi cho thông báo lỗi, ưu tiên detectedIndex rồi mới fallback sang questionNumber.
const buildQuestionRef = (draft, fallbackIndex) => {
    // detectedIndex: vị trí câu parser đếm được trong luồng đọc hiện tại.
    // fallbackIndex: chỉ số dự phòng truyền từ bên ngoài khi draft thiếu thông tin.
    // Ưu tiên detectedIndex vì phản ánh đúng vị trí thực tế của câu trong quá trình parse.
    const detectedIndex = Number(draft?.detectedIndex) || Number(fallbackIndex) || 0;

    // questionNumber: số câu lấy từ nhãn người dùng soạn trong file (ví dụ "Câu 12").
    // Dùng để đối chiếu với detectedIndex và hiển thị ngữ cảnh rõ hơn khi lệch số.
    const questionNumber = Number(draft?.questionNumber) || 0;

    // Chỉ xem là "nhãn Câu n" đáng tin cậy khi parser match trực tiếp mẫu "Câu n".
    const hasExplicitQuestionLabel = draft?.questionNumberSource === 'explicit';

    // Trường hợp có đủ cả 2 chỉ số nhưng không trùng nhau:
    // báo cả vị trí parse được và nhãn trong đề để người soạn dễ dò lỗi.
    if (detectedIndex > 0 && questionNumber > 0 && hasExplicitQuestionLabel && detectedIndex !== questionNumber) {
        return `Dòng/Câu thứ ${detectedIndex} (nhãn "Câu ${questionNumber}")`;
    }

    // Trường hợp phổ biến: có vị trí parser nhận diện được.
    if (detectedIndex > 0) {
        return `Dòng/Câu thứ ${detectedIndex}`;
    }

    // Không có detectedIndex nhưng còn nhãn số câu trong file.
    if (questionNumber > 0 && hasExplicitQuestionLabel) {
        return `Câu ${questionNumber}`;
    }

    // Fallback cuối: không suy ra được vị trí nào đáng tin cậy.
    return 'Dòng/Câu không xác định';
};

// Chốt 1 câu hỏi tạm thành dữ liệu chuẩn, validate và bỏ vào output.
const finalizeQuestion = (draft, output, rowNumber) => {
    if (!draft) {
        return;
    }

    const questionRef = buildQuestionRef(draft, rowNumber);

    // Chốt lại nội dung câu hỏi; nếu thiếu thì tạo nhãn tạm để cảnh báo.
    let question = getTrimmedString(draft.questionText);
    if (!question) {
        question = `Question ${rowNumber}`;
        output.warnings.push(`${questionRef}: không có nội dung ngay sau tiêu đề, đã dùng nhãn tạm "${question}".`);
    }

    const cleanedType = getTrimmedString(draft.questionType).toLowerCase();

    if (cleanedType === 'text' || draft.sampleAnswers.length > 0) {
        // Nhanh danh cho cau tra loi ngan (text input).
        if (draft.sampleAnswers.length === 0) {
            output.errors.push(`${questionRef}: câu hỏi text cần "Đáp án mẫu".`);
            return;
        }

        output.questions.push({
            id: output.questions.length + 1,
            rowNumber: Number(draft.detectedIndex) || rowNumber,
            questionNumber: Number(draft.questionNumber) || null,
            questionNumberSource: draft.questionNumberSource || 'detected',
            question,
            questionHtml: getTrimmedString(draft.questionHtml),
            sectionId: draft.sectionId,
            sectionTitle: getTrimmedString(draft.sectionTitle),
            sectionTitleHtml: getTrimmedString(draft.sectionTitleHtml),
            sectionContent: getTrimmedString(draft.sectionContent),
            sectionContentHtml: getTrimmedString(draft.sectionContentHtml),
            sharedContextText: getTrimmedString(draft.sharedContextText),
            sharedContextHtml: getTrimmedString(draft.sharedContextHtml),
            questionType: 'text',
            options: [],
            optionsHtml: [],
            correctAnswers: draft.sampleAnswers,
            explanation: getTrimmedString(draft.explanation),
            explanationHtml: getTrimmedString(draft.explanationHtml),
            hint: getTrimmedString(draft.hint),
            hintHtml: getTrimmedString(draft.hintHtml),
            textInputConfig: draft.textInputConfig,
        });
        return;
    }

    const optionLetters = draft.optionOrder.filter((letter, index, arr) => arr.indexOf(letter) === index);

    if (optionLetters.length < 2) {
        // Da co thong bao noi ro nguyen nhan (dong dap an rong) thi khong bao lai
        // bang thong diep chung chung nua.
        if (draft.emptyOptionRowReported) {
            return;
        }

        const recognizedOptions = optionLetters.length > 0 ? 'đáp án: ' + optionLetters.join(', ') : 'không có đáp án mẫu';
        output.errors.push(
            `${questionRef}: Chương trình hiện nhận diện được: ${recognizedOptions}. Thiếu "Đáp án mẫu: ..." với câu điền đáp án hoặc cần ít nhất 2 lựa chọn theo mẫu "A. ...", "B. ..." với câu trắc nghiệm. `
            
        );
        return;
    }

    const options = optionLetters.map((letter) => getTrimmedString(draft.optionsByLetter[letter]));
    const optionsHtml = optionLetters.map((letter, index) => {
        // Neu khong co html rieng thi fallback sang text da escape.
        const htmlValue = getTrimmedString(draft.optionsByLetterHtml[letter]);
        return htmlValue || escapeHtml(options[index] || '');
    });
    const hasEmptyOption = options.some((value, index) => {
        if (value !== '') {
            return false;
        }

        const htmlValue = optionsHtml[index] || '';
        return !hasVisibleHtmlContent(htmlValue);
    });

    if (hasEmptyOption) {
        const emptyLetters = optionLetters.filter((letter, index) => {
            const optionText = options[index] || '';
            if (optionText !== '') {
                return false;
            }

            const htmlValue = optionsHtml[index] || '';
            return !hasVisibleHtmlContent(htmlValue);
        });

        output.errors.push(
            `${questionRef}: Có đáp án rỗng ở lựa chọn ${emptyLetters.join(', ') || 'không xác định'}. `
            + 'Vui lòng điền nội dung sau nhãn đáp án (ví dụ: "A. ...").'
        );
        return;
    }

    let correctAnswers = [];
    // Uu tien danh sach explicit (Correct: ...), neu khong co thi dung dau *.
    if (draft.explicitCorrectLetters.length > 0) {
        correctAnswers = [...draft.explicitCorrectLetters];
    } else if (draft.starredCorrectLetters.length > 0) {
        correctAnswers = [...draft.starredCorrectLetters];
    }

    correctAnswers = correctAnswers.filter((letter, index, arr) => arr.indexOf(letter) === index);

    if (correctAnswers.length === 0) {
        // Giu backward-compatible: mac dinh A neu de khong danh dau dap an dung.
        correctAnswers = ['A'];
        output.warnings.push(`${questionRef}: Không đánh dấu * vào đáp án đúng tạm mặc định đáp án A.`);
    }

    const unknownCorrectLetter = correctAnswers.find((letter) => !optionLetters.includes(letter));
    if (unknownCorrectLetter) {
        output.errors.push(`${questionRef}: Đáp án đúng ${unknownCorrectLetter} không tồn tại trong danh sách lựa chọn.`);
        return;
    }

    let questionType = cleanedType;
    if (!questionType) {
        // Suy luan loai cau hoi khi nguoi soan de khong ghi Type.
        if (optionLetters.length === 2) {
            questionType = 'boolean';
        } else if (correctAnswers.length > 1) {
            questionType = 'multiple';
        } else {
            questionType = 'single';
        }
    }

    if (!['single', 'multiple', 'boolean'].includes(questionType)) {
        output.errors.push(`${questionRef}: Loại câu hỏi "${questionType}" không hợp lệ.`);
        return;
    }

    output.questions.push({
        id: output.questions.length + 1,
        rowNumber: Number(draft.detectedIndex) || rowNumber,
        questionNumber: Number(draft.questionNumber) || null,
        questionNumberSource: draft.questionNumberSource || 'detected',
        question,
        questionHtml: getTrimmedString(draft.questionHtml),
        sectionId: draft.sectionId,
        sectionTitle: getTrimmedString(draft.sectionTitle),
        sectionTitleHtml: getTrimmedString(draft.sectionTitleHtml),
        sectionContent: getTrimmedString(draft.sectionContent),
        sectionContentHtml: getTrimmedString(draft.sectionContentHtml),
        sharedContextText: getTrimmedString(draft.sharedContextText),
        sharedContextHtml: getTrimmedString(draft.sharedContextHtml),
        questionType,
        options,
        optionsHtml,
        correctAnswers,
        explanation: getTrimmedString(draft.explanation),
        explanationHtml: getTrimmedString(draft.explanationHtml),
        hint: getTrimmedString(draft.hint),
        hintHtml: getTrimmedString(draft.hintHtml),
    });
};

// Số vị trí tối đa liệt kê ra khi gom nhiều câu cùng một cảnh báo.
const MAX_GROUPED_REFS = 3;

// Ghép danh sách vị trí của một nhóm cảnh báo thành nhãn ngắn gọn.
// Các vị trí dạng "Dòng/Câu thứ n" được gộp số lại: "Dòng/Câu thứ 2, 5, 9".
const formatGroupedRefs = (refs) => {
    if (refs.length === 1) {
        return refs[0];
    }

    const numbers = refs.map((ref) => {
        const match = ref.match(/^Dòng\/Câu thứ (\d+)/);
        return match ? match[1] : null;
    });

    if (numbers.every(Boolean)) {
        const shown = numbers.slice(0, MAX_GROUPED_REFS).join(', ');
        const remaining = numbers.length - MAX_GROUPED_REFS;

        return remaining > 0
            ? `Dòng/Câu thứ ${shown} (và ${remaining} câu khác)`
            : `Dòng/Câu thứ ${shown}`;
    }

    const shown = refs.slice(0, MAX_GROUPED_REFS).join('; ');
    const remaining = refs.length - MAX_GROUPED_REFS;

    return remaining > 0 ? `${shown} (và ${remaining} vị trí khác)` : shown;
};

// ============================================================
// GOM CÁC CẢNH BÁO/LỖI TRÙNG NỘI DUNG
// Một lỗi soạn đề lặp lại ở nhiều câu (ví dụ "không đánh dấu *") trước đây sinh
// ra mỗi câu một dòng, chiếm hết chỗ hiển thị và đẩy các cảnh báo quan trọng ra
// ngoài. Ở đây các mục có CÙNG nội dung được gom lại thành một dòng kèm danh
// sách vị trí, giữ nguyên thứ tự xuất hiện đầu tiên.
// ============================================================
const groupDuplicateIssues = (issues) => {
    const refsByBody = new Map();
    const bodyOrder = [];

    (Array.isArray(issues) ? issues : []).forEach((issue) => {
        const text = getTrimmedString(issue);
        if (!text) {
            return;
        }

        // Nhãn vị trí luôn đứng trước dấu ": " đầu tiên (vd 'Dòng/Câu thứ 2: ...').
        const separatorIndex = text.indexOf(': ');
        const ref = separatorIndex > 0 ? text.slice(0, separatorIndex) : '';
        const body = separatorIndex > 0 ? text.slice(separatorIndex + 2) : text;

        if (!refsByBody.has(body)) {
            refsByBody.set(body, []);
            bodyOrder.push(body);
        }

        const refs = refsByBody.get(body);
        if (ref && !refs.includes(ref)) {
            refs.push(ref);
        }
    });

    return bodyOrder.map((body) => {
        const refs = refsByBody.get(body);
        return refs.length > 0 ? `${formatGroupedRefs(refs)}: ${body}` : body;
    });
};

// Core parser: doc danh sach block (text + html) va xay dung danh sach cau hoi.
const parseQuizFromBlocks = (blocks) => {
    // Chuan hoa du lieu dau vao ve mang line gom text + html.
    // Mot block co the chua nhieu dong (Word xuong dong bang <br>), can tach truoc khi parse.
    const rawLines = blocks.flatMap((block) => {
    const blockText = String(block.text || '');
    const blockHtml = getTrimmedString(block.html);

     if (blockHtml) {
        // Bang la khoi nguyen ven, KHONG duoc tach theo <br> vi <br> co the nam
        // sau ben trong cac o <td> — tach se lam vo cau truc <table>.
        if (/^<table\b/i.test(blockHtml)) {
            return [{
                text: stripLeadingBullets(htmlToPlainText(blockHtml)).trim(),
                html: blockHtml,
            }];
        }

        const htmlParts = splitHtmlByLineBreaks(blockHtml);
        return htmlParts.map((htmlPart) => ({
            text: stripLeadingBullets(htmlToPlainText(htmlPart)).trim(),
            html: htmlPart,
        }));
    }

    return blockText.split('\n').map((part) => ({
        text: stripLeadingBullets(getTrimmedString(part)).trim(),
        html: '',
    }));
});
    

    // Gom cac dong trong lien tiep thanh 1 dong de tranh nhan ban khoang trang.
    const lines = [];
    rawLines.forEach((line) => {
        const normalized = {
            text: getTrimmedString(line.text),
            html: getTrimmedString(line.html),
        };

        const isEmpty = !normalized.text && !normalized.html;
        const previous = lines.length > 0 ? lines[lines.length - 1] : null;
        const previousIsEmpty = previous ? !previous.text && !previous.html : false;

        if (isEmpty && previousIsEmpty) {
            return;
        }

        lines.push(normalized);
    });

    const output = {
        questions: [],
        warnings: [],
        errors: [],
        documentTitle: '',
        documentTitleHtml: '',
    };

    let current = null;
    // Chi so dong cuoi cung cua khoi dap an danh so (1./2./3./4.) dang xu ly.
    // Cac dong nam trong khoi nay luon duoc hieu la lua chon, khong phai cau hoi moi.
    let numericOptionRunUntil = -1;
    let detectedQuestionCount = 0;
    let activeSection = null;
    let activeSectionQuestionCount = 0;
    let sectionCounter = 0;
    let documentTitle = '';
    let documentTitleHtml = '';
    let pendingSharedContextText = '';
    let pendingSharedContextHtml = '';

    const appendDocumentPrelude = (text, html) => {
        documentTitle = appendTextLine(documentTitle, text);
        documentTitleHtml = appendHtml(documentTitleHtml, html || escapeHtml(text));
    };

    const appendPendingSharedContext = (text, html) => {
        pendingSharedContextText = appendTextLine(pendingSharedContextText, text);
        pendingSharedContextHtml = appendHtml(pendingSharedContextHtml, html || escapeHtml(text));
    };

    // Ghi mot canh bao kem nhan vi tri cua cau hoi dang xu ly.
    const pushQuestionWarning = (message) => {
        output.warnings.push(`${buildQuestionRef(current, detectedQuestionCount || 1)}: ${message}`);
    };

    const pushQuestionError = (message) => {
        output.errors.push(`${buildQuestionRef(current, detectedQuestionCount || 1)}: ${message}`);
    };

    // Gan noi dung cho mot lua chon. Neu ky tu dap an da ton tai thi noi dung cu
    // se bi ghi de (hanh vi cu) nhung phai canh bao vi day la mat du lieu am tham.
    const applyOption = ({ letter, text, html, isCorrect }) => {
        if (Object.prototype.hasOwnProperty.call(current.optionsByLetter, letter)) {
            pushQuestionWarning(
                `Lựa chọn ${letter} xuất hiện nhiều lần. Nội dung cũ "${summarizeLine(current.optionsByLetter[letter])}" `
                + `đã bị thay bằng "${summarizeLine(text)}". Vui lòng kiểm tra lại cách đánh dấu A/B/C/D.`
            );
        }

        current.optionsByLetter[letter] = text;
        current.optionsByLetterHtml[letter] = html;
        current.optionOrder.push(letter);
        if (isCorrect) {
            current.starredCorrectLetters.push(letter);
        }
    };

    // Cau hoi va cac lua chon viet chung 1 dong se KHONG duoc nhan (co y, de loai
    // cac dong sai format). Nhung phai bao cho nguoi soan de biet vi sao mat cau.
    const warnInlineOptionsOnSameLine = (inlineOptions) => {
        if (!inlineOptions || inlineOptions.options.length < 2) {
            return;
        }

        const [firstOption] = inlineOptions.options;
        // Chi canh bao khi thuc su giong mot bo dap an: bat dau tu A va co du 3
        // lua chon tro len, hoac marker A dung sau mot bien ro rang (dau cau/xuong
        // dong). Tranh bao nham voi van xuoi kieu "... trong doan A. Nam da ...".
        const looksLikeOptionList = firstOption.letter === 'A'
            && (inlineOptions.options.length >= 3 || firstOption.strength >= 2);

        if (!looksLikeOptionList) {
            return;
        }

        pushQuestionWarning(
            'Phát hiện nội dung câu hỏi và các lựa chọn viết chung một dòng '
            + `("${summarizeLine(inlineOptions.questionText)}" rồi tới "${firstOption.letter}. ..."). `
            + 'Chương trình chỉ nhận đáp án khi mỗi lựa chọn nằm trên một dòng riêng — '
            + 'vui lòng tách "A. ...", "B. ..." xuống dòng.'
        );
    };

    lines.forEach((lineObj, lineIndex) => {
        let line = normalizeParserLine(lineObj.text);
        let lineHtml = lineObj.html;

        if (!line && !lineHtml) {
            // Gap dong trong: reset mode ve question (truong hop dang noi option thi giu nguyen).
            // Giai thich/goi y cung giu nguyen: chung chi ket thuc khi gap nhan moi, cau moi
            // hoac tieu de phan. Dong trong o day thuong chi la <br> thua (Shift+Enter) trong
            // doan van; reset ve question se noi phan giai thich con lai len noi dung cau hoi.
            if (current && !['option', 'explanation', 'hint'].includes(current.mode)) {
                current.mode = 'question';
            }
            return;
        }

        const sectionHeader = parseSectionHeaderLine(line);
        if (sectionHeader) {
            // Gap tieu de section moi thi chot cau dang mo (neu co), sau do bat dau section moi.
            if (current) {
                finalizeQuestion(current, output, current.questionNumber || detectedQuestionCount || 1);
                current = null;
            }

            sectionCounter += 1;
            activeSection = createSection(
                sectionCounter,
                sectionHeader.title,
                lineHtml || escapeHtml(sectionHeader.title)
            );
            activeSectionQuestionCount = 0;

            // Neu cung dong co luon cau hoi dau tien thi tiep tuc parse phan con lai.
            const trailingText = getTrimmedString(sectionHeader.trailingText);
            if (!trailingText) {
                return;
            }

            line = trailingText;
            lineHtml = escapeHtml(trailingText);
        }

        // Truoc khi phan loai dong: kiem tra day co phai diem bat dau cua mot khoi
        // dap an danh so (Word doi list A/B/C/D thanh 1/2/3/4) hay khong.
        if (
            current
            && current.optionOrder.length === 0
            && current.mode !== 'explanation'
            && current.mode !== 'hint'
            && lineIndex > numericOptionRunUntil
            && numericOptionRunItemRegex.test(line)
            && Number(line.match(numericOptionRunItemRegex)[1]) === 1
        ) {
            const numericRun = detectNumericOptionRun(lines, lineIndex);
            if (numericRun) {
                numericOptionRunUntil = numericRun.endIndex;
            }
        }

        // Dong nam trong khoi dap an danh so khong duoc coi la cau hoi moi hay y nho.
        const isInsideNumericOptionRun = Boolean(current) && lineIndex <= numericOptionRunUntil;

        const questionStart = isInsideNumericOptionRun
            ? null
            : (matchQuestionStart(line) || matchAutoNumberedQuestionStart(line, current));
        if (questionStart) {
            // Bat dau cau moi: finalize cau cu roi tao draft cau tiep theo.
            if (current) {
                finalizeQuestion(current, output, current.questionNumber || detectedQuestionCount || 1);
            }

            detectedQuestionCount += 1;
            const questionNumber = Number(questionStart.questionNumber) || detectedQuestionCount;
            const questionText = questionStart.questionText || '';
            const questionTextIndex = questionText ? line.indexOf(questionText) : -1;
            const questionPrefix = questionTextIndex >= 0 ? line.slice(0, questionTextIndex) : '';
            const questionHtml = lineHtml
                ? stripLeadingTextFromHtml(lineHtml, questionPrefix)
                : escapeHtml(questionText);

            current = createEmptyQuestion(
                questionNumber,
                questionText,
                activeSection,
                detectedQuestionCount,
                questionStart.questionNumberSource || 'detected',
                {
                    text: pendingSharedContextText,
                    html: pendingSharedContextHtml,
                }
            );
            pendingSharedContextText = '';
            pendingSharedContextHtml = '';

            if (activeSection && current.sectionId === activeSection.id) {
                activeSectionQuestionCount += 1;
            }

            current.questionHtml = questionHtml || escapeHtml(questionText);

            const inlineOptions = extractInlineOptions(questionText, questionHtml);
            if (inlineOptions && !inlineOptions.questionText) {
                // Chi nhan inline khi marker dap an nam ngay dau dong (khong co phan
                // noi dung cau hoi dung truoc), vd "Cau 1: A. ... B. ...".
                const normalizedQuestionText = `Question ${questionNumber}`;
                current.questionText = normalizedQuestionText;
                current.questionHtml = inlineOptions.questionHtml || escapeHtml(normalizedQuestionText);

                inlineOptions.options.forEach((option) => applyOption({
                    letter: option.letter,
                    text: option.text,
                    html: getTrimmedString(option.html) || escapeHtml(option.text),
                    isCorrect: option.isCorrect,
                }));

                const lastOption = inlineOptions.options[inlineOptions.options.length - 1];
                current.mode = 'option';
                current.currentOptionLetter = lastOption ? lastOption.letter : null;
            } else {
                warnInlineOptionsOnSameLine(inlineOptions);
            }
            return;
        }

        if (!current) {
            // Neu dang trong section thi gom noi dung thu/chu de chung de hien thi truoc nhom cau hoi.
            if (activeSection && activeSectionQuestionCount === 0) {
                activeSection.content = appendTextLine(activeSection.content, line);
                activeSection.contentHtml = appendHtml(activeSection.contentHtml, lineHtml || escapeHtml(line));
                return;
            }

            if (detectedQuestionCount === 0) {
                // Tieu de mo dau tai lieu chi gom cac dong truoc khi bat dau section "PHAN ...".
                appendDocumentPrelude(line, lineHtml || escapeHtml(line));
                return;
            }

            // Noi dung chen giua cac cau hoi se duoc dua vao shared context cua cau tiep theo.
            appendPendingSharedContext(line, lineHtml || escapeHtml(line));
            return;
        }

        const typeMatch = line.match(typeRegex);
        if (typeMatch) {
            // Doc cau hinh Type: single/multiple/boolean/text.
            current.questionType = getTrimmedString(typeMatch[1]).toLowerCase();
            current.mode = 'question';
            current.currentOptionLetter = null;
            return;
        }

        const sampleAnswerMatch = line.match(sampleAnswerRegex);
        if (sampleAnswerMatch) {
            // "Dap an mau" dung dau cham phay de ho tro nhieu dap an chap nhan duoc.
            const sampleAnswers = getTrimmedString(sampleAnswerMatch[1])
                .split(';')
                .map((answer) => answer.trim())
                .filter((answer) => answer !== '')
                .map((answer) => answer.toLowerCase());

            current.sampleAnswers = sampleAnswers;
            current.questionType = 'text';
            current.mode = 'question';
            current.currentOptionLetter = null;
            return;
        }

        const explanationMatch = line.match(explanationRegex);
        if (explanationMatch) {
            // Khong escape gia tri giai thich/hint o day vi block nay co the chua MathML/LaTeX,
            // ma `escapeHtml` se bi bien thanh text literal va mat hinh thuc render.
            const explanationText = getTrimmedString(explanationMatch[1]);

            // "Tra loi: B" trong nhieu de that ra la khoa dap an chu khong phai loi giai.
            // Parser van xu ly nhu giai thich (giu hanh vi cu) nhung phai canh bao vi
            // nguoi soan de se bi mat dap an dung ma khong biet.
            if (answerLabelRegex.test(line) && isAnswerKeyLikeText(explanationText)) {
                pushQuestionWarning(
                    `Dòng "${summarizeLine(line)}" đang được hiểu là phần giải thích. `
                    + `Nếu "${explanationText}" là đáp án đúng, hãy ghi "Đáp án đúng: ${explanationText}" `
                    + 'hoặc đánh dấu "*" ngay trước lựa chọn đúng.'
                );
            }

            current.explanation = explanationText;
            current.explanationHtml = stripLeadingLabelFromHtml(lineHtml, line, explanationText) || explanationText;
            current.mode = 'explanation';
            current.currentOptionLetter = null;
            return;
        }

        const hintMatch = line.match(hintRegex);
        if (hintMatch) {
            // Cho phep ghi chu goi y de hien thi cho nguoi lam bai khi can.
            const hintText = getTrimmedString(hintMatch[1]);
            current.hint = hintText;
            current.hintHtml = stripLeadingLabelFromHtml(lineHtml, line, hintText) || hintText;
            current.mode = 'hint';
            current.currentOptionLetter = null;
            return;
        }

        const correctAnswerMatch = line.match(correctAnswerRegex);
        if (correctAnswerMatch) {
            // Ghi de explicit dap an dung, uu tien hon dau * o option.
            const rawCorrectValue = getTrimmedString(correctAnswerMatch[1]);
            const parsedCorrectLetters = parseCorrectLetters(rawCorrectValue);

            // parseCorrectLetters chi tach bang dau phay. Cac cach viet khac
            // ("A B", "A;C", "Dung") se bi bo qua am tham roi mac dinh ve A,
            // nen phai canh bao ro cho nguoi soan de.
            if (rawCorrectValue && parsedCorrectLetters.length === 0) {
                pushQuestionWarning(
                    `Không đọc được ký tự đáp án nào từ "Đáp án đúng: ${summarizeLine(rawCorrectValue)}". `
                    + 'Chỉ chấp nhận các ký tự A/B/C/D, nhiều đáp án phân tách bằng dấu phẩy (ví dụ: "Đáp án đúng: A, C").'
                );
            } else if (countStandaloneAnswerLetters(rawCorrectValue) > parsedCorrectLetters.length) {
                pushQuestionWarning(
                    `"Đáp án đúng: ${summarizeLine(rawCorrectValue)}" chỉ nhận diện được ${parsedCorrectLetters.join(', ')}. `
                    + 'Nhiều đáp án phải phân tách bằng dấu phẩy (ví dụ: "Đáp án đúng: A, C").'
                );
            }

            current.explicitCorrectLetters = parsedCorrectLetters;
            current.mode = 'question';
            current.currentOptionLetter = null;
            return;
        }

        // FIX: kiem tra dong huong dan mo dau section moi (vd "Mark the letter A, B, C, or D...")
        // TRUOC KHI xu ly che do explanation/hint. Truoc day check nay nam sau 2 nhanh mode
        // 'explanation'/'hint' (von return som), nen khi dong huong dan cua cau tiep theo xuat
        // hien ngay sau dong "Giai thich:"/"Goi y:" cua cau hien tai, no bi nuot luon vao noi
        // dung giai thich/goi y thay vi duoc tach ra lam sharedContext cho cau ke tiep.
        const normalizedBreakLine = stripLeadingDecorators(line);
        if (sectionBreakRegex.test(normalizedBreakLine) && current.optionOrder.length > 0) {
            // Chot cau hien tai, dong huong dan se duoc gan vao cau ke tiep de render dung vi tri.
            finalizeQuestion(current, output, current.questionNumber || detectedQuestionCount || 1);
            current = null;
            appendPendingSharedContext(line, lineHtml || escapeHtml(line));
            return;
        }

        if (current.mode === 'explanation') {
            // Khi dang o phan giai thich, moi dong ke tiep deu thuoc giai thich.
            current.explanation = getTrimmedString(`${current.explanation} ${line}`);
            current.explanationHtml = appendHtml(current.explanationHtml, lineHtml || escapeHtml(line));
            return;
        }

        if (current.mode === 'hint') {
            // Tuong tu explanation, phan goi y duoc noi tiep theo tung dong.
            current.hint = getTrimmedString(`${current.hint} ${line}`);
            current.hintHtml = appendHtml(current.hintHtml, lineHtml || escapeHtml(line));
            return;
        }

        const inlineOptions = extractInlineOptions(line, lineHtml);
        if (inlineOptions && !inlineOptions.questionText) {
            // Chi chap nhan inline khi marker dap an xuat hien ngay dau dong.

            inlineOptions.options.forEach((option) => applyOption({
                letter: option.letter,
                text: option.text,
                html: getTrimmedString(option.html) || escapeHtml(option.text),
                isCorrect: option.isCorrect,
            }));

            const lastOption = inlineOptions.options[inlineOptions.options.length - 1];
            current.mode = 'option';
            current.currentOptionLetter = lastOption ? lastOption.letter : null;
            return;
        }

        // Dong vua co noi dung vua co day dap an -> khong nhan, canh bao de nguoi
        // soan de tach dong (xem warnInlineOptionsOnSameLine).
        if (inlineOptions && current.optionOrder.length === 0) {
            warnInlineOptionsOnSameLine(inlineOptions);
        }

        // Dong chi gom nhan "A. B. C. D." ma khong lua chon nao co noi dung.
        if (!inlineOptions && current.optionOrder.length === 0 && isEmptyInlineOptionRow(line, lineHtml)) {
            const letters = findInlineOptionMarkers(line).map((marker) => marker.letter).join(', ');
            pushQuestionError(
                `Các lựa chọn ${letters} đều không có nội dung. `
                + 'Nếu đáp án là hình vẽ, rất có thể hình được vẽ bằng công cụ Shape/SmartArt của Word — '
                + 'trình duyệt không đọc được loại hình này. Hãy chuyển từng hình thành ảnh PNG/JPG rồi chèn lại '
                + '(hoặc tách mỗi đáp án xuống một dòng riêng rồi chèn ảnh).'
            );
            current.emptyOptionRowReported = true;
            return;
        }

        const subItemMatch = line.match(subItemRegex);
        if (subItemMatch && current.optionOrder.length === 0) {
            // Cac muc a), b) truoc khi co option A/B/C/D duoc coi la phan noi dung cau hoi.
            const subItemContent = getTrimmedString(subItemMatch[2]);
            const subItemLabel = `${getTrimmedString(subItemMatch[1]).toLowerCase()}.`;
            const subItemLine = getTrimmedString(`${subItemLabel} ${subItemContent}`);
            current.questionText = getTrimmedString(`${current.questionText} ${subItemLine}`);
            current.questionHtml = appendHtml(current.questionHtml, lineHtml || escapeHtml(subItemLine));
            current.subItemCount += 1;
            current.mode = 'question';
            current.currentOptionLetter = null;
            return;
        }

        const numericSubItemMatch = line.match(numericSubItemRegex);
        if (numericSubItemMatch && current.optionOrder.length === 0 && !isInsideNumericOptionRun) {
            // Word co the bi convert danh sach a/b/c thanh 1/2/3; doi lai de hien thi dung dang sap xep.
            const originalNumber = Number(numericSubItemMatch[1]) || 0;
            const inferredIndex = originalNumber > 0 ? originalNumber : current.subItemCount + 1;
            const alphaLabel = toAlphaLabel(inferredIndex) || toAlphaLabel(current.subItemCount + 1) || 'a';
            const subItemContent = getTrimmedString(numericSubItemMatch[2]);
            const subItemLine = getTrimmedString(`${alphaLabel}. ${subItemContent}`);
            current.questionText = getTrimmedString(`${current.questionText} ${subItemLine}`);
            // Co y override html theo text da chuan hoa de preview khong bi hien 1., 2., 3.
            current.questionHtml = appendHtml(current.questionHtml, escapeHtml(subItemLine));
            current.subItemCount += 1;
            current.mode = 'question';
            current.currentOptionLetter = null;
            return;
        }

        const option = parseOptionLine(line);
        if (option) {
            // Neu chua co option nao va letter khong phai A thi bo qua: tranh nham phan
            // noi dung cau hoi co dang "D. hinh lap phuong..." thanh dap an D.
            if (current.optionOrder.length === 0 && option.letter !== 'A') {
                current.questionText = getTrimmedString(`${current.questionText} ${line}`);
                current.questionHtml = appendHtml(current.questionHtml, lineHtml || escapeHtml(line));
                return;
            }
            // Dang "A ..." (khong co dau . : ) - sau ky tu dap an) rat de nuot nham
            // mot dong van xuoi cua de bai thanh lua chon. Giu nguyen hanh vi cu
            // nhung canh bao khi dong do dai giong van xuoi hon la mot dap an ngan.
            if (!option.hasSeparator && looksLikeProseLine(option.text)) {
                pushQuestionWarning(
                    `Dòng "${summarizeLine(line)}" thiếu dấu phân cách sau ký tự đáp án nên được hiểu là lựa chọn ${option.letter}. `
                    + `Nếu đây là đáp án, hãy viết "${option.letter}. ..."; nếu là nội dung câu hỏi, hãy sửa để dòng không bắt đầu bằng "${option.letter} ".`
                );
            }

            // Parse mot dong option binh thuong va cap nhat vi tri option hien tai de noi dong.
            applyOption({
                letter: option.letter,
                text: option.text,
                html: stripLeadingTextFromHtml(lineHtml, option.markerText) || escapeHtml(option.text),
                isCorrect: option.isCorrect,
            });
            current.mode = 'option';
            current.currentOptionLetter = option.letter;
            return;
        }

        if (current.mode === 'option' && current.currentOptionLetter) {
            // Dong tiep theo option se duoc noi vao option vua parse (option nhieu dong).
            const previous = current.optionsByLetter[current.currentOptionLetter] || '';
            current.optionsByLetter[current.currentOptionLetter] = getTrimmedString(`${previous} ${line}`);

            const previousHtml = current.optionsByLetterHtml[current.currentOptionLetter] || '';
            current.optionsByLetterHtml[current.currentOptionLetter] = appendHtml(previousHtml, lineHtml || escapeHtml(line));
            return;
        }

        // Mac dinh: coi dong hien tai la phan mo rong cua noi dung cau hoi.
        current.questionText = getTrimmedString(`${current.questionText} ${line}`);
        current.questionHtml = appendHtml(current.questionHtml, lineHtml || escapeHtml(line));
    });

    if (current) {
        // Finalize cau cuoi cung sau khi duyet het lines.
        finalizeQuestion(current, output, current.questionNumber || detectedQuestionCount || 1);
    }

    return {
        ...output,
        // Gom cac muc trung noi dung lai truoc khi tra ve cho man hinh kiem tra.
        errors: groupDuplicateIssues(output.errors),
        warnings: groupDuplicateIssues(output.warnings),
        documentTitle: getTrimmedString(documentTitle),
        documentTitleHtml: getTrimmedString(documentTitleHtml),
        stats: {
            totalRows: detectedQuestionCount,
            validRows: output.questions.length,
            invalidRows: detectedQuestionCount - output.questions.length,
        },
    };
};

// Parser fallback cho truong hop chi co raw text (mat dinh dang rich text).
const parseQuizFromRawText = (rawText) => {
    // Raw text khong giu duoc rich text nen html duoc tao tu text da escape.
    const lines = normalizeLineBreaks(rawText)
        .split('\n')
        .map((line) => ({ text: line, html: escapeHtml(line) }));

    return parseQuizFromBlocks(lines);
};

const isLikelyUnreadableImageDataUrl = async (dataUrl) => {
    const input = getTrimmedString(dataUrl);
    if (!input || typeof document === 'undefined' || typeof Image === 'undefined') {
        return false;
    }

    const img = await new Promise((resolve) => {
        const image = new Image();
        const timer = setTimeout(() => resolve(null), 1200);

        image.onload = () => {
            clearTimeout(timer);
            resolve(image);
        };
        image.onerror = () => {
            clearTimeout(timer);
            resolve(null);
        };
        image.src = input;
    });

    if (!img || img.width <= 0 || img.height <= 0) {
        return true;
    }

    const sampleWidth = Math.min(img.width, 320);
    const sampleHeight = Math.min(img.height, 320);
    const canvas = document.createElement('canvas');
    canvas.width = sampleWidth;
    canvas.height = sampleHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return false;
    }

    ctx.drawImage(img, 0, 0, sampleWidth, sampleHeight);
    const data = ctx.getImageData(0, 0, sampleWidth, sampleHeight).data;
    const totalPixels = sampleWidth * sampleHeight;

    if (totalPixels === 0) {
        return true;
    }

    let visiblePixels = 0;
    for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        const red = data[i];
        const green = data[i + 1];
        const blue = data[i + 2];

        if (alpha > 8 && (red < 250 || green < 250 || blue < 250)) {
            visiblePixels += 1;
        }
    }

    const visibleRatio = visiblePixels / totalPixels;
    return visibleRatio < 0.003;
};

// ============================================================
// KIEM TRA KHA NANG HIEN THI CUA TOAN BO ANH — CHAY SONG SONG
// isLikelyUnreadableImageDataUrl phai cho trinh duyet decode anh (timeout 1200ms).
// Truoc day no duoc await ngay trong convertImage, ma mammoth goi convertImage
// tuan tu => de 30 anh loi co the treo ~36 giay. Nay tat ca chay song song va co
// cache theo data URL de khong decode lai cung mot anh xuat hien nhieu lan.
// ============================================================
const runImageReadabilityChecks = async (pendingChecks, pushImageIssue) => {
    if (!Array.isArray(pendingChecks) || pendingChecks.length === 0) {
        return;
    }

    const checkCache = new Map();
    const results = await Promise.all(pendingChecks.map(async (item) => {
        if (!checkCache.has(item.dataUrl)) {
            checkCache.set(item.dataUrl, isLikelyUnreadableImageDataUrl(item.dataUrl));
        }

        return {
            item,
            isUnreadable: await checkCache.get(item.dataUrl),
        };
    }));

    results.forEach(({ item, isUnreadable }) => {
        if (!isUnreadable) {
            return;
        }

        pushImageIssue(
            item.imageNumber,
            `Ảnh trong câu có định dạng ${item.formatLabel} có thể không hiển thị đúng. Vui lòng đổi sang PNG/JPG, nếu là công thức toán học đổi sang LaTex hoặc equation, nếu là công thức toán học đổi sang equation hoặc latex rồi tải lại.`
        );
    });
};

// So canh bao dinh dang toi da hien thi, tranh lam ngap danh sach canh bao.
const MAX_CONVERSION_WARNINGS = 3;

// ============================================================
// LỌC/DỊCH THÔNG BÁO KỸ THUẬT CỦA MAMMOTH
// Mammoth trả về thông báo tiếng Anh dành cho lập trình viên. Nhiều thông báo
// nói lại đúng điều parser đã cảnh báo (và cảnh báo của parser còn kèm số câu),
// nên nếu hiện hết thì người soạn đề thấy cùng một vấn đề lặp 2-3 lần.
// Bảng dưới: replacement = null nghĩa là BỎ HẲN, còn lại là câu tiếng Việt thay thế.
// ============================================================
const CONVERSION_MESSAGE_RULES = [
    {
        // Đã có cảnh báo riêng "Ảnh trong câu có định dạng ... " kèm số câu.
        pattern: /image of type .+ is unlikely to display/i,
        replacement: null,
    },
    {
        // Style Word lạ: chỉ ảnh hưởng cách trình bày, không mất nội dung.
        pattern: /unrecognised (?:paragraph|run|table|numbering) style/i,
        replacement: null,
    },
    {
        // Đối tượng nhúng OLE (công thức Equation 3.0, biểu đồ Excel...).
        // Word luôn kèm một ảnh xem trước (thường là WMF) nên phần này thường
        // đã được cảnh báo qua ảnh; chỉ nói một lần, bằng tiếng Việt.
        pattern: /OLEObject/i,
        replacement: 'File có đối tượng nhúng của Word (công thức Equation, biểu đồ...) mà trình duyệt không đọc được. '
            + 'Nếu phần đó cần hiển thị, hãy chuyển thành ảnh PNG rồi tải lại.',
    },
    {
        pattern: /unrecognised element was ignored/i,
        replacement: 'File có phần tử Word không đọc được nên đã bị bỏ qua. Kiểm tra lại xem nội dung có bị thiếu không.',
    },
];

const translateConversionMessage = (message) => {
    const rule = CONVERSION_MESSAGE_RULES.find((item) => item.pattern.test(message));
    if (!rule) {
        return message;
    }

    return rule.replacement;
};

// Gom canh bao do mammoth tra ve khi doc DOCX (style la, phan tu bo qua...).
const buildConversionWarnings = (messages) => {
    const uniqueMessages = Array.from(new Set(
        (Array.isArray(messages) ? messages : [])
            .map((item) => getTrimmedString(typeof item === 'string' ? item : item?.message))
            .filter(Boolean)
            .map(translateConversionMessage)
            // null = thong bao trung voi canh bao khac -> bo han.
            .filter(Boolean)
    ));

    if (uniqueMessages.length === 0) {
        return [];
    }

    const warnings = uniqueMessages
        .slice(0, MAX_CONVERSION_WARNINGS)
        .map((message) => `Định dạng DOCX: ${message}`);

    if (uniqueMessages.length > MAX_CONVERSION_WARNINGS) {
        warnings.push(`Định dạng DOCX: còn ${uniqueMessages.length - MAX_CONVERSION_WARNINGS} cảnh báo định dạng khác khi đọc file.`);
    }

    return warnings;
};

const doesQuestionContainImageIndex = (question, imageIndex) => {
    const idx = Number(imageIndex);
    if (!Number.isFinite(idx) || idx <= 0) {
        return false;
    }

    const imageIndexRegex = new RegExp(`data-docx-image-index\\s*=\\s*["']${idx}["']`, 'i');
    const optionsHtml = Array.isArray(question?.optionsHtml) ? question.optionsHtml : [];
    const htmlFragments = [
        question?.questionHtml,
        question?.sectionTitleHtml,
        question?.sectionContentHtml,
        question?.sharedContextHtml,
        question?.explanationHtml,
        question?.hintHtml,
        ...optionsHtml,
    ].map((item) => String(item || ''));

    return htmlFragments.some((fragment) => imageIndexRegex.test(fragment));
};

const buildImageWarningsWithQuestionRef = (questions, imageIssueMap) => {
    if (!(imageIssueMap instanceof Map) || imageIssueMap.size === 0) {
        return [];
    }

    const questionList = Array.isArray(questions) ? questions : [];
    const warnings = [];

    const buildQuestionRefFromParsedQuestion = (question, fallbackImageIndex) => {
        if (!question) {
            return `Ảnh thứ ${fallbackImageIndex}`;
        }

        const detectedIndex = Number(question.rowNumber) || 0;
        const questionNumber = Number(question.questionNumber) || 0;
        const hasExplicitQuestionLabel = question.questionNumberSource === 'explicit';

        if (detectedIndex > 0 && questionNumber > 0 && hasExplicitQuestionLabel && detectedIndex !== questionNumber) {
            return `Dòng/Câu thứ ${detectedIndex} (nhãn "Câu ${questionNumber}")`;
        }

        if (detectedIndex > 0) {
            return `Dòng/Câu thứ ${detectedIndex}`;
        }

        if (questionNumber > 0 && hasExplicitQuestionLabel) {
            return `Câu ${questionNumber}`;
        }

        return `Ảnh thứ ${fallbackImageIndex}`;
    };

    imageIssueMap.forEach((messages, imageIndex) => {
        const safeMessages = Array.isArray(messages) ? messages : [];
        if (safeMessages.length === 0) {
            return;
        }

        const ownerQuestion = questionList.find((question) => doesQuestionContainImageIndex(question, imageIndex));
        const questionRef = buildQuestionRefFromParsedQuestion(ownerQuestion, imageIndex);

        const uniqueMessages = Array.from(new Set(safeMessages.map((message) => getTrimmedString(message)).filter(Boolean)));
        uniqueMessages.forEach((message) => {
            warnings.push(`${questionRef}: ${message}`);
        });
    });

    return Array.from(new Set(warnings));
};

// Entry point: uu tien parse HTML de giu dinh dang, sau do fallback sang raw text.
export const parseQuizDocxFromArrayBuffer = async (arrayBuffer) => {
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('File DOCX rong hoac khong doc duoc du lieu.');
    }

    const {
        arrayBuffer: patchedArrayBuffer,
        placeholders,
        pictImages,
        imageSizes,
    } = await patchDocxOmmlIntoLatex(arrayBuffer);
    const imageWarnings = [];
    const imageIssueMap = new Map();
    // Hang doi cac anh can kiem tra "co hien thi duoc khong", chay song song sau
    // khi mammoth convert xong thay vi chan tung anh mot.
    const pendingImageChecks = [];
    // Anh <w:pict> da duoc chen truc tiep o buoc patch va danh so tu 1,
    // nen bo dem cua mammoth phai bat dau sau day de khong trung so.
    let imageIndex = pictImages.length;

    const pushImageIssue = (index, message) => {
        const safeIndex = Number(index);
        if (!Number.isFinite(safeIndex) || safeIndex <= 0) {
            return;
        }

        const normalizedMessage = getTrimmedString(message);
        if (!normalizedMessage) {
            return;
        }

        const existing = imageIssueMap.get(safeIndex) || [];
        existing.push(normalizedMessage);
        imageIssueMap.set(safeIndex, existing);
    };

    // Anh <w:pict> khong di qua convertImage cua mammoth nen phai tu kiem dinh dang.
    pictImages.forEach((image) => {
        const isVectorFormat = image.contentType === 'image/x-emf' || image.contentType === 'image/x-wmf';
        if (isVectorFormat) {
            pushImageIssue(
                image.imageNumber,
                `Ảnh trong câu có định dạng ${image.contentType} có thể không hiển thị đúng. Vui lòng đổi sang PNG/JPG, nếu là công thức toán học đổi sang LaTex hoặc equation rồi tải lại.`
            );
        }
    });

    // Parse theo HTML truoc de giu format nhu dam/nghieng/gach chan va hinh anh.
    const htmlResult = await mammoth.convertToHtml(
        { arrayBuffer: patchedArrayBuffer },
        {
            // Mammoth mac dinh bo qua underline, can map ro rang de giu <u> khi render.
            styleMap: ['u => u'],
            convertImage: mammoth.images.imgElement(async (image) => {
                imageIndex += 1;
                const currentImageNumber = imageIndex;
                const contentType = getTrimmedString(image?.contentType).toLowerCase();
                const formatLabel = contentType || 'không rõ định dạng';
                const isVectorFormat = contentType === 'image/x-emf' || contentType === 'image/emf' || contentType === 'image/x-wmf' || contentType === 'image/wmf';
                // Kich thuoc Word hien thi (xem tagImagesWithWordSize). alt luon duoc tra ve
                // alt text goc, ke ca khi anh loi, de ma tam khong lo ra man hinh.
                const wordSize = imageSizes.get(getTrimmedString(image?.altText));
                const wordSizeAttributes = wordSize
                    ? { alt: wordSize.altText, style: buildImageSizeStyle(wordSize) }
                    : {};

                if (isVectorFormat) {
                    pushImageIssue(
                        currentImageNumber,
                        `Ảnh trong câu có định dạng ${formatLabel} có thể không hiển thị đúng. Vui lòng đổi sang PNG/JPG, nếu là công thức toán học đổi sang LaTex hoặc equation rồi tải lại.`
                    );
                }

                let base64 = '';
                try {
                    // Chuyen anh thanh data URL de render ngay trong noi dung quiz.
                    base64 = await image.read('base64');
                } catch (error) {
                    pushImageIssue(
                        currentImageNumber,
                        `Ảnh trong câu có định dạng ${formatLabel} không đọc được dữ liệu. Vui lòng đổi sang PNG/JPG, nếu là công thức toán học đổi sang LaTex hoặc equation rồi tải lại.`
                    );
                    return {
                        src: '',
                        'data-docx-image-index': String(currentImageNumber),
                        ...wordSizeAttributes,
                    };
                }

                if (!base64) {
                    pushImageIssue(
                        currentImageNumber,
                        `${formatLabel} rỗng hoặc lỗi dữ liệu. Vui lòng đổi sang PNG/JPG, nếu là công thức toán học đổi sang LaTex hoặc equation rồi tải lại.`
                    );
                    return {
                        src: '',
                        'data-docx-image-index': String(currentImageNumber),
                        ...wordSizeAttributes,
                    };
                }

                const dataUrl = `data:${image.contentType};base64,${base64}`;

                // Khong kiem tra anh trang/loi ngay tai day nua: moi anh phai cho
                // decode (toi 1200ms neu anh hong) va mammoth goi convertImage TUAN TU,
                // nen file nhieu anh se treo UI rat lau. Xep hang de chay song song
                // sau khi convert xong (xem runImageReadabilityChecks).
                if (!isVectorFormat) {
                    pendingImageChecks.push({ imageNumber: currentImageNumber, dataUrl, formatLabel });
                }

                return {
                    src: dataUrl,
                    'data-docx-image-index': String(currentImageNumber),
                    ...wordSizeAttributes,
                };
            }),
        }
    );

    // Giai ma va kiem tra toan bo anh cung luc (thay vi noi tiep trong convertImage).
    await runImageReadabilityChecks(pendingImageChecks, pushImageIssue);

    // Canh bao dinh dang do mammoth bao ve (style la, phan tu khong doc duoc...)
    // truoc day bi bo qua hoan toan nen nguoi soan de khong biet file co van de.
    const conversionWarnings = buildConversionWarnings(htmlResult.messages);

    const blocks = replaceDocxMathTokensInBlocks(
        extractBlocksFromHtml(htmlResult.value || ''),
        placeholders
    );

    if (blocks.length > 0) {
        const parsedFromHtml = parseQuizFromBlocks(blocks);
        const resolvedImageWarnings = buildImageWarningsWithQuestionRef(parsedFromHtml.questions, imageIssueMap);
        imageWarnings.push(...resolvedImageWarnings);

        if (imageWarnings.length > 0 || conversionWarnings.length > 0) {
            // Gom lai lan nua vi canh bao anh cua nhieu cau co the trung noi dung.
            parsedFromHtml.warnings = groupDuplicateIssues([
                ...imageWarnings,
                ...conversionWarnings,
                ...parsedFromHtml.warnings,
            ]);
        }

        if (parsedFromHtml.stats.totalRows > 0) {
            // Neu HTML parser nhan duoc cau hoi hop le thi tra ve ngay.
            return parsedFromHtml;
        }
    }

    // Fallback an toàn: nếu tài liệu không parse được theo block HTML thì quay về raw text như cũ.
    const rawResult = await mammoth.extractRawText({ arrayBuffer: patchedArrayBuffer });
    const rawText = getTrimmedString(
        replaceDocxMathTokens(rawResult.value, placeholders, (placeholder) => placeholder.textReplacement)
    );

    if (!rawText) {
        throw new Error('File DOCX khong co noi dung.');
    }

    const parsed = parseQuizFromRawText(rawText);

    if (imageWarnings.length > 0 || conversionWarnings.length > 0) {
        parsed.warnings = groupDuplicateIssues([
            ...imageWarnings,
            ...conversionWarnings,
            ...parsed.warnings,
        ]);
    }

    if (parsed.stats.totalRows === 0) {
        throw new Error('Khong tim thay cau hoi theo quy uoc "Cau n:" trong file DOCX.');
    }

    // Tra ket qua fallback khi parse HTML that bai.
    return parsed;
};

// Export parser raw text de test/diagnose hoac dung cho nguon khong phai DOCX.
export const parseQuizDocxFromRawText = parseQuizFromRawText;
